/**
 * WATER-TRUTH-1 — deterministic post-generation guard for water-use claims.
 *
 * The economic SED carries modeled water-use scenarios with explicit low/high
 * endpoints. A report may repeat those endpoints (including the rounded form
 * rendered into the prompt), but it must not interpolate a new subject-site
 * quantity or describe a modeled endpoint as measured, permitted, requested,
 * actual, or committed use. Policy-valid comparable-project observations are
 * allowed only when the prose explicitly attributes the value to a comparable.
 *
 * This guard is deliberately narrow:
 *   - only reports whose economic-evidence projection includes `water`, plus
 *     the source-role-only types (Test Fit), which get the source-stated role
 *     audit and nothing else;
 *   - the source-stated role audit runs with or without a calculated screen;
 *   - only water rates with an explicit hourly/daily/annual unit, plus explicit
 *     consumptive-percent claims;
 *   - explicit denial of calculated phase water cases recorded in the SED;
 *   - no inference from MW, PUE, or cooling coefficients.
 *
 * It is a detector, not a calculator. When the deterministic screen is absent
 * it does not manufacture a replacement baseline, but it still fails closed
 * on numeric water-regulatory thresholds lacking qualifying primary evidence.
 */
import type {
  ComparableWaterObservationValue,
  WaterUseScenarioResult,
  WaterUseScreeningResult,
} from '../../types/economicContribution';
import type { SiteEvidenceDocument } from '../../types/siteEvidence';
import type { DataSource, SiteRagReport } from '../../types/siteRag';
import { reportProjectsEconomicFacet } from '../economicContribution/sedEconomicEvidence';
import { isMunicipalOperatingRateContext, municipalOperatingScopeMismatch, municipalOperatingScopeQualification } from '../grounding/municipalWaterSourceRoles';
import { renderGenerationImpactProjection, reportFullText } from './reportTextExtract';
import { siteResourceSourceRowsFromSed, siteResourceViewFromSed } from '../sed/siteResourceDocumentProjection';
import type { SiteResourceKey, SiteResourceSourceRow } from '../../types/siteResourceEvidence';
import { extractNumberedRefIds, maskNumberedRefTokens } from '../citationTokens';
import {
  classifyWaterQuantityRole,
  DEFAULT_WHOLE_SITE_WATER_TOLERANCE_GAL_PER_DAY,
  matchWholeSiteWaterRoles,
  wholeSiteWaterLedger,
  type WaterQuantityRole,
  type WholeSiteWaterLedgerEntry,
  type WholeSiteWaterLedgerKey,
} from '../water/waterQuantityRoles';
import {
  declaredWaterConsumptionProvenance,
  type DeclaredWaterConsumptionProvenance,
} from '../generationImpact/declaredWaterConsumptionProvenance';

export type WaterUseScreeningFindingKind =
  | 'unsupported-site-water-value'
  | 'municipal-operating-scope'
  | 'unsupported-water-regulatory-threshold-provenance'
  | 'modeled-water-mislabeled'
  | 'water-statistic-mislabeled'
  | 'water-end-use-mislabeled'
  | 'water-flow-basis-mislabeled'
  | 'comparable-water-stage-mislabeled'
  | 'unsupported-consumptive-percentage'
  | 'missing-water-screen-disclosure'
  /** Calculated phase cases exist in the frozen SED but prose denies them. */
  | 'recorded-phase-water-screen-absence'
  /** P3.4 — one sentence presents a scenario-duty figure and a screen-band figure as the same quantity. */
  | 'water-basis-conflated'
  /** P3.6 — a screen band correctly quoted but attached to a load outside the screen's MW scope. */
  | 'water-band-out-of-scope-load'
  /**
   * W6 — a whole-site source quantity (site allocation, average/peak project
   * demand, allocation margin) presented as a cooling-process quantity or a
   * consumptive-use limit, or a zero modeled equipment/process subtotal
   * presented as whole-site water.
   */
  | 'water-source-role-conflated'
  | 'guard-execution-failed';

export interface WaterUseScreeningFinding {
  kind: WaterUseScreeningFindingKind;
  severity: 'block';
  sectionId: 'water-use-screening';
  message: string;
  excerpt: string;
  observed: string;
  expected: string;
}

interface ParsedWaterQuantity {
  raw: string;
  /** Exact span in the parsed field; repeated values can have different actors. */
  start: number;
  end: number;
  excerpt: string;
  /** Sentence / flattened-record segment that semantically owns the value. */
  context: string;
  /** The side of a comparison owning this value; provider context cannot exempt the other side. */
  providerContext: string;
  /** Individual water/load clause when a sentence compares multiple phases. */
  loadContext: string;
  period: 'hour' | 'day' | 'year';
  multiplier: number;
  lowRaw: string;
  highRaw?: string;
  low: number;
  high: number;
}

interface AllowedBand {
  scenarioId: string;
  label: string;
  /** Exact input scope of this modeled case, not the primary screen's scope. */
  loadRange?: WaterUseScreeningResult['loadRange'];
  period: 'hour' | 'day' | 'year';
  low: number;
  high: number;
  boundary: WaterUseScenarioResult['boundary'];
  flowBasis: WaterUseScenarioResult['flowBasis'];
  measure:
    | 'scenario-average'
    | 'scenario-annual'
    | 'adiabatic-evaporation'
    | 'adiabatic-makeup'
    /** P3.3 — an exact figure from the frozen promoted-scenario duty table. */
    | 'generation-duty'
    /** P3.6 — the scenario's 100%-load cooling sensitivity (was produced, rendered nowhere, admitted nowhere). */
    | 'scenario-full-load';
  /** W6 — which promoted-duty row this band is (promoted water-duty bands only). */
  dutyComponent?: 'generation' | 'ccs' | 'data-center' | 'total' | 'declared-input';
  /**
   * W6 — admitted only for the deterministic projection text itself, never for
   * authored prose: a declared "available capacity" that equals a whole-site
   * source quantity (and the headroom derived from it) is not a consumptive
   * capacity an author may restate.
   */
  projectionTextOnly?: boolean;
}

interface AllowedComparablePoint {
  projectId: string;
  facilityName: string;
  value: number;
  period: 'hour' | 'day' | 'year';
  stage: ComparableWaterObservationValue['stage'];
}

interface AllowedConsumptivePercentage {
  low: number;
  high: number;
  sourceDocId: string;
}

const NUMBER = String.raw`\d[\d,]*(?:\.\d+)?`;
const RANGE_SEPARATOR = String.raw`(?:\s*(?:-|\u2013|\u2014|\bto\b)\s*|\s*\|\s*)`;

/** MGD is kept separate so `m` cannot be confused with the million prefix. */
const MGD_PATTERN = new RegExp(
  String.raw`\b(${NUMBER})(?:${RANGE_SEPARATOR}(${NUMBER}))?\s*(?:MGD|Mgal\s*\/\s*d)\b`,
  'gi',
);
const GALLON_RATE_PATTERN = new RegExp(
  String.raw`\b(${NUMBER})(?:${RANGE_SEPARATOR}(${NUMBER}))?\s*(million\s+)?(?:US\s+)?(?:gallons?|gal)\s*(?:\/|per\s+)(hour|hourly|hr|h|day|daily|d|year|yr|annum)\b`,
  'gi',
);
const GPD_PATTERN = new RegExp(
  String.raw`\b(${NUMBER})(?:${RANGE_SEPARATOR}(${NUMBER}))?\s*gpd\b`,
  'gi',
);
const CONSUMPTIVE_PERCENT_PATTERN = new RegExp(
  String.raw`\b(${NUMBER})(?:${RANGE_SEPARATOR}(${NUMBER}))?\s*%\s+(?:net\s+)?(?:consumption|consumptive\s+(?:use|loss|share|fraction|rate))\b`,
  'gi',
);

/** P3.4 — words that present two figures as one quantity. */
const WATER_BASIS_EQUIVALENCE_PATTERN = /\b(?:equivalent(?:\s+to)?|equals?|equal\s+to|the\s+same\s+as|matches|corresponds?\s+to|reconcil\w*|consistent\s+with|in\s+line\s+with|confirms?|agrees?\s+with|i\.e\.)\b/i;
const MODELED_CAVEAT_PATTERN = /\b(?:model(?:ed|led)|screening|scenario|sensitivity|estimate(?:d)?|illustrative|benchmark)\b/i;
const EXPLICIT_COMPARABLE_PATTERN = /\b(?:comparable|peer(?:\s+project)?|comparison\s+(?:site|project)|public[- ]record\s+(?:site|project)|another\s+(?:site|facility|project))\b/i;
const NEGATED_STATUS_PATTERN = /\b(?:not|isn['’]?t|is\s+not|are\s+not|does\s+not|do\s+not|neither|nor|without|rather\s+than|instead\s+of)\b[^.\n]{0,120}\b(?:actual|measured|metered|permitted|authorized|approved|requested|committed|guaranteed|design(?:ed)?)\b/i;
const FORBIDDEN_STATUS_PATTERN = /\b(?:(?:actual|measured|metered|permitted|authorized|approved|requested|committed|guaranteed|design(?:ed)?)(?:\s+(?:actual|measured|metered|permitted|authorized|approved|requested|committed|guaranteed|design(?:ed)?)){0,2}\s+(?:site\s+)?(?:water(?:\s+(?:use|demand|withdrawal|consumption|quantity|supply|allocation))?|withdrawal|consumption|use|demand|quantity|allocation|entitlement)|(?:water(?:\s+(?:use|demand|withdrawal|consumption|quantity|supply|allocation))?|withdrawal|consumption|allocation|entitlement)\s+(?:is\s+|was\s+|as\s+)?(?:actual|measured|metered|permitted|authorized|approved|requested|committed|guaranteed|design(?:ed)?))\b/i;
const DEFINITIVE_SITE_USE_PATTERN = /\b(?:(?:the|this)\s+)?(?:site|project|campus|facility|development)\s+(?:will|shall|is\s+expected\s+to|requires?|needs?)\s+(?:use|consume|withdraw|draw|need|require)|\b(?:water|cooling[- ]water)\s+(?:use|demand|consumption|withdrawal)\s+(?:is|equals|totals|will\s+be)\b/i;

/**
 * A provider or treatment system's total capacity is not a subject-site water
 * demand claim. Those external facts remain under the ordinary grounding and
 * citation gates because the economic water screen does not model utility
 * production capacity. Both a provider actor and system-capacity framing are
 * required so an ambiguous "6 MGD available" statement is not waved through.
 */
const EXTERNAL_WATER_PROVIDER_PATTERN = /\b(?:utility|waterworks|water\s+(?:utility|system|department|division|district|authority|provider|supplier|treatment\s+(?:plant|facility)|plant)|wastewater\s+(?:utility|system|service|department|division|district|authority|provider|treatment\s+(?:plant|facility)|plant)|treatment\s+(?:plant|facility)|wwtp|municipal\s+(?:system|utility|plant|provider|supplier)|city\s+(?:system|utility|plant|water\s+department)|county\s+(?:system|utility|plant|water\s+department)|parish\s+(?:system|utility|plant|water\s+department))\b/i;
const EXTERNAL_SYSTEM_CAPACITY_PATTERN = /\b(?:plant|treatment\s+(?:plant|facility)|wwtp)\s+(?:is\s+|was\s+)?permitted\s+(?:for|to\s+(?:produce|treat|process))\b|\b(?:total\s+system|systemwide|system|plant|treatment|production|rated|nameplate|spare|surplus|installed|design)\s+(?:water\s+|wastewater\s+)?capacity\b|\b(?:capacity|rated|designed)\s+(?:at|for|to\s+(?:produce|treat|process|deliver))\b|\b(?:can|could)\s+(?:supply|deliver|produce|treat|process)\b|\b(?:available|surplus)\s+(?:water\s+)?supply\b|\b(?:water\s+)?supply\s+(?:is\s+)?available\b|\bavailable\s+systemwide\b|\b(?:average|daily|annual|reported|current)(?:\s+(?:average|daily|annual|reported|current)){0,2}\s+(?:water\s+)?production\s+(?:of|is|was|at)\b|\b(?:average\s+daily\s+)?processing\s+of\b|\bpermitted\s+capacity\s+of\b/i;

/** Explicitly assigns a quantity, entitlement, or availability to this site. */
const SUBJECT_SITE_ALLOCATION_PATTERN = /\b(?:(?:the|this|planned|proposed)\s+)?(?:site|project|campus|facility|development|data\s+cent(?:er|re))\b(?:[^.\n]|\.(?=\d)){0,140}\b(?:has|have|receives?|allocated|allocation|reserved|reservation|entitled|entitlement|available|availability|committed|dedicated|permitted|approved|will\s+(?:use|consume|withdraw|draw|receive|require)|requires?|needs?)\b|\b(?:available|allocated|reserved|dedicated|committed|permitted|approved|suppl(?:y|ies|ied)|deliv(?:er|ers|ered)|provid(?:e|es|ed))\b(?:[^.\n]|\.(?=\d)){0,100}\b(?:to|for)\s+(?:(?:the|this|planned|proposed)\s+)?(?:site|project|campus|facility|development|data\s+cent(?:er|re))\b/i;

/**
 * General permit thresholds describe when a rule applies; they are not a
 * quantity assigned to the subject site. All three signals are required so a
 * passing mention of a permit near a project demand cannot evade the guard.
 */
const REGULATORY_AUTHORIZATION_PATTERN = /\b(?:permits?|permitt(?:ed|ing)|authori[sz](?:ation|ed|ing)|approval|registration|notification|reporting|regulat(?:e|es|ed|ing|ion|ory)|manag(?:e|es|ed|ing|ement)|oversight)\b/i;
const REGULATORY_RULE_PATTERN = /\b(?:requires?|required|must|is\s+subject\s+to|are\s+subject\s+to|applies?|applicable|triggers?|triggered)\b/i;
const REGULATORY_THRESHOLD_PATTERN = /\b(?:threshold|exceed(?:s|ed|ing)?|above|over|more\s+than|greater\s+than|at\s+least|upon\s+reaching|trigger(?:s|ed|ing)?)\b/i;
const GENERIC_REGULATED_ACTOR_PATTERN = /\b(?:a|an|any|each|every)\s+(?:site|project|campus|facility|development|data\s+cent(?:er|re))\b/gi;
const GENERIC_REGULATORY_FORM_PATTERN = /\b(?:all|any|each|every)\s+(?:surface\s+or\s+groundwater\s+|water\s+)?(?:withdrawals?|uses?|users?|facilities?)\b|\b(?:a|an)\s+(?:(?:water|surface|groundwater|withdrawal|use)[- ]?\s*){0,4}permit\b[^.\n]{0,180}\b(?:for|covering)\s+(?:(?:any|all|each|every)\s+)?(?:surface\s+or\s+groundwater\s+|water\s+)?(?:withdrawals?|uses?)\b/i;
const SUBJECT_SITE_REGULATED_ACTIVITY_PATTERN = /\b(?:this|the|proposed|planned)\s+(?:site|project|campus|facility|development|data\s+cent(?:er|re))(?:['’]s)?\s+(?:water\s+|cooling[- ]water\s+)?(?:uses?|withdrawals?|demand|consumption)\b/i;
const NAMED_WATER_REGULATOR_PATTERN = /\b(?:LDNR|LDEQ|DNR|DEQ|DEP|DEC|DWR|TCEQ|EPA|water\s+resources?\s+(?:department|division|agency|board|commission|authority|office)|(?:department|division|agency|board|commission|authority|office)\s+of\s+(?:natural\s+resources|environmental\s+(?:quality|protection)|water\s+resources?))\b/i;

/**
 * A legal threshold is allowed only when the citation attached to that exact
 * sentence resolves to recognizably primary water-regulatory evidence. The
 * report reference shape has no stored quote/locator, so this is deliberately
 * conservative: metadata may establish an eligible provenance class, but it
 * must never promote AI research, DAM material, an upload, or a generic
 * government dataset into proof of a statute/rule threshold.
 */
const DISALLOWED_REGULATORY_REFERENCE_FIELD_PATTERN = /(?:^|[._:/-])(?:site[-_]?research|dam|docs?|documents?|uploads?|knowledge)(?:$|[._:/-])|(?:site|user|uploaded|developer|source)(?:docs?|documents?|uploads?)/i;
const DISALLOWED_REGULATORY_REFERENCE_SOURCE_PATTERN = /\b(?:AI[- ]synthesi[sz]ed|AI\s+site-specific\s+research|Datacrate\s+Model|curated\s+knowledge|knowledge\s+base|secondary\s+(?:source|research|summary)|generic\s+literature|user[- ]uploaded|uploaded\s+(?:file|document)|developer[- ](?:provided|supplied|submitted)|developer\s+(?:document|materials?)|inference-only|estimated\s+research)\b/i;
const OFFICIAL_GOVERNMENT_URL_PATTERN = /^https?:\/\/[^/?#]*\.gov(?:[/:?#]|$)/i;
const OFFICIAL_GOVERNMENT_SOURCE_PATTERN = /\b(?:United\s+States|U\.?S\.?|State\s+of\s+[A-Z]|federal|state|county|parish|city)\b[^\n]{0,100}\b(?:department|division|agency|board|commission|authority|office|bureau)\b|\b(?:department|division|agency|board|commission|authority|office|bureau)\s+of\s+[A-Za-z]|\b(?:LDNR|LDEQ|DNR|DEQ|DEP|DEC|DWR|TCEQ|EPA)\b/i;
const WATER_REGULATORY_SOURCE_SCOPE_PATTERN = /\b(?:water|groundwater|surface[- ]?water|withdrawals?|wells?|water\s+rights?)\b/i;
const WATER_REGULATORY_SOURCE_AUTHORITY_PATTERN = /\b(?:statutes?|legislation|administrative\s+code|codes?|regulations?|regulatory|rules?|permits?|registrations?|reporting|notifications?|authori[sz]ations?|requirements?|water\s+rights?|management\s+(?:program|requirements?|rules?|regulations?)|title\s+\d+|chapter\s+\d+|section\s+\d+|CFR)\b/i;

export const WATER_REGULATORY_THRESHOLD_FALLBACK =
  'Permit applicability must be confirmed with the responsible authority.';

/**
 * Remove numeric permit-threshold sentences from an unverified research field
 * before it reaches a reader-visible datasheet. The post-generation report
 * guard validates authored narrative, but `datasets.siteResearch` is rendered
 * separately by ExtendedDataSection and therefore needs the same fail-closed
 * treatment at that projection boundary.
 *
 * Callers must use this only for unverified / AI-research text. A threshold
 * backed by a qualifying primary rule belongs in the evidence catalog and the
 * authored report path, where same-context citation provenance can be checked.
 */
export function neutralizeUnverifiedWaterRegulatoryThresholdText(
  text: string | null | undefined,
): string | null | undefined {
  if (typeof text !== 'string' || !text.trim()) return text;
  const sentences = text.match(/[^.!?]+(?:[.!?]+|$)/g) ?? [text];
  let removedThreshold = false;
  const retained = sentences.filter((sentence) => {
    const hasUnsupportedThreshold = parseWaterQuantities(sentence).some(
      (quantity) => isGeneralRegulatoryThresholdFact(quantity.context),
    );
    if (hasUnsupportedThreshold) removedThreshold = true;
    return !hasUnsupportedThreshold;
  });
  if (!removedThreshold) return text;
  const remainder = retained.join(' ').replace(/\s+/g, ' ').trim();
  return remainder
    ? `${WATER_REGULATORY_THRESHOLD_FALLBACK} ${remainder}`
    : WATER_REGULATORY_THRESHOLD_FALLBACK;
}

/**
 * A consumptive percentage from a frozen developer document may be repeated
 * only as an attributed developer-document claim. It is not promoted to a
 * modeled, measured, permitted, or otherwise independently verified value.
 */
const DEVELOPER_DOCUMENT_ATTRIBUTION_PATTERN = /\b(?:developer|owner|applicant|operator)(?:['’]s)?(?:[- ](?:provided|supplied|submitted|authored))?\s+(?:documents?|materials?|records?|plans?|stud(?:y|ies)|claims?|submittals?)\b|\b(?:according\s+to|per)\s+(?:the\s+)?(?:developer|owner|applicant|operator)(?:['’]s)?\b/i;

const COMPARABLE_STAGE_PATTERNS: Readonly<Record<ComparableWaterObservationValue['stage'], RegExp>> = {
  'design-estimate': /\b(?:design(?:ed)?|estimate(?:d)?|model(?:ed|led)|project(?:ed|ion)|propos(?:ed|al)|illustrative|screening)\b/i,
  'application-request': /\b(?:application|appl(?:y|ied|ies|ying)\s+for|request(?:ed|ing)?|sought|seeking)\b/i,
  'permit-limit': /\b(?:permit(?:ted)?|authori[sz](?:ed|ation)|approved|allocation|entitlement|limit)\b/i,
  'reported-actual': /\b(?:reported\s+actual|actual(?:ly)?|measured|metered|observed|recorded|consumed|withdrew|used)\b/i,
};

const CONTEXT_MW_PATTERN = /\b(\d[\d,]*(?:\.\d+)?)\s*MW\b/gi;

/** First MW figure in the sentence that lies outside the screen's recorded interval, else null. */
function outOfScopeLoadInContext(
  context: string,
  loadRange: { low: number; high: number; basis?: string },
): number | null {
  let effectiveContext = context;
  const cleanContext = context.replace(/<!--[\s\S]*?-->/g, (m) => ' '.repeat(m.length));
  const colonIdx = cleanContext.indexOf(':');
  if (colonIdx > 0) {
    const preColon = context.slice(0, colonIdx);
    const postColon = context.slice(colonIdx + 1);
    const hasWaterInPreColon =
      /\b(?:MGD|(?:million\s+)?(?:gallons?|gal)\s*(?:\/|per\s+)(?:day|d|year|yr|hour|hr)|gpd)\b/i.test(
        preColon,
      );
    if (!hasWaterInPreColon) {
      const postPhases = new Set(
        [...postColon.matchAll(/\bphase\s*#?\s*(\d+)\b/gi)].map((m) => m[1]!),
      );
      if (postPhases.size > 0) {
        const filteredPreColon = preColon.replace(
          /\bphase\s*#?\s*(\d+)\b(?:\s*\/\s*[\w-]+)?\s*\([^)]*\)/gi,
          (full, phaseNum: string) => (postPhases.has(phaseNum) ? full : ' '.repeat(full.length)),
        );
        effectiveContext = filteredPreColon + ':' + postColon;
      }
    }
  }
  CONTEXT_MW_PATTERN.lastIndex = 0;
  const mentions: Array<{ mw: number; index: number; end: number }> = [];
  let match: RegExpExecArray | null;
  while ((match = CONTEXT_MW_PATTERN.exec(effectiveContext)) != null) {
    const mw = Number((match[1] ?? '').replace(/,/g, ''));
    if (!Number.isFinite(mw)) continue;
    mentions.push({ mw, index: match.index, end: match.index + match[0].length });
  }
  const hasInScopeMention = mentions.some((m) => m.mw >= loadRange.low && m.mw <= loadRange.high);
  for (const m of mentions) {
    if (m.mw >= loadRange.low && m.mw <= loadRange.high) continue;
    if (hasInScopeMention) {
      const nearby = context.slice(Math.max(0, m.index - 28), Math.min(context.length, m.end + 28));
      // When the sentence states the in-scope load alongside its PUE-derived
      // companion load (e.g. "706 MW facility load (assumed IT 565 MW at PUE 1.25)"),
      // do not reject the companion figure as an out-of-scope load case.
      if (
        (loadRange.basis === 'facility' || loadRange.basis === undefined) &&
        /\bIT\b/i.test(nearby) &&
        m.mw >= loadRange.low / 1.4 - 2 &&
        m.mw <= loadRange.high / 1.1 + 2
      ) {
        continue;
      }
      if (
        (loadRange.basis === 'it' || loadRange.basis === undefined) &&
        /\bfacility\b/i.test(nearby) &&
        m.mw >= loadRange.low * 1.1 - 2 &&
        m.mw <= loadRange.high * 1.4 + 2
      ) {
        continue;
      }
    }
    return m.mw;
  }
  return null;
}

function finiteNonNegative(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0;
}

function compactExcerpt(text: string, start: number, end: number): string {
  const lineStart = Math.max(text.lastIndexOf('\n', start - 1) + 1, 0);
  const lineEndAt = text.indexOf('\n', end);
  const lineEnd = lineEndAt < 0 ? text.length : lineEndAt;
  const line = text.slice(lineStart, lineEnd).replace(/\s+/g, ' ').trim();
  if (line.length <= 420) return line;
  const relativeStart = start - lineStart;
  const from = Math.max(0, relativeStart - 170);
  return `${from > 0 ? '…' : ''}${line.slice(from, from + 400)}${from + 400 < line.length ? '…' : ''}`;
}

/**
 * Scope semantic classifiers to the sentence (or pipe-delimited flattened
 * record field) containing the number. A whole report field is often stored
 * on one physical line, so line-wide markers can belong to an adjacent claim.
 */
function quantityContextBounds(text: string, start: number, end: number, options: {
  isolateComparison?: boolean;
  isolatePhaseLoads?: boolean;
  isolateStatistics?: boolean;
} = {}): { start: number; end: number } {
  const { isolateComparison, isolatePhaseLoads, isolateStatistics } = options;
  const lineStart = Math.max(text.lastIndexOf('\n', start - 1) + 1, 0);
  const lineEndAt = text.indexOf('\n', end);
  const lineEnd = lineEndAt < 0 ? text.length : lineEndAt;
  const before = text.slice(lineStart, start);
  const after = text.slice(end, lineEnd);

  const lastSentenceDot = (s: string): number => {
    for (let i = s.length - 2; i >= 0; i -= 1) {
      if (s[i] === '.' && s[i + 1] === ' ' && s[i - 1] !== '.') return i;
    }
    return -1;
  };
  const firstSentenceDot = (s: string): number => {
    for (let i = 0; i < s.length - 1; i += 1) {
      if (s[i] === '.' && s[i + 1] === ' ' && s[i - 1] !== '.' && s[i + 2] !== '.') return i;
    }
    return -1;
  };
  const leftBoundaries = [
    lastSentenceDot(before),
    before.lastIndexOf('! '),
    before.lastIndexOf('? '),
    before.lastIndexOf(' | '),
    ...(isolateStatistics ? [before.lastIndexOf('; ')] : []),
  ];
  const left = Math.max(...leftBoundaries);
  let contextStart = left < 0 ? lineStart : lineStart + left + (before.slice(left, left + 3) === ' | ' ? 3 : 2);

  const rightBoundaries = [
    firstSentenceDot(after),
    after.indexOf('! '),
    after.indexOf('? '),
    after.indexOf(' | '),
    ...(isolateStatistics ? [after.indexOf('; ')] : []),
  ].filter((index) => index >= 0);
  const right = rightBoundaries.length > 0 ? Math.min(...rightBoundaries) : after.length;
  let contextEnd = end + right + (/^[.!?]/.test(after.slice(right)) ? 1 : 0);
  if (isolateComparison) {
    const originalStart = contextStart;
    const comparison = /\b(?:exceeds?|outstrips?|compared\s+(?:with|to)|versus|greater\s+than|less\s+than)\b|,\s*(?:whereas|while)\b/gi;
    for (const match of text.slice(originalStart, contextEnd).matchAll(comparison)) {
      const separatorStart = originalStart + match.index;
      const separatorEnd = separatorStart + match[0].length;
      if (separatorEnd <= start) contextStart = separatorEnd;
      else if (separatorStart >= end) { contextEnd = separatorStart; break; }
    }
  }
  if (isolatePhaseLoads) {
    const hasPair = (value: string) => (/\b\d[\d,.]*\s*MW\b/i.test(value) || /\bphase\s*#?\s*\d+\b/i.test(value))
      && /\b(?:MGD|(?:million\s+)?(?:gallons?|gal)\s*(?:\/|per\s+)(?:day|d|year|yr|hour|hr))\b/i.test(value);
    const sameUnclosedParen = (fromIdx: number, toIdx: number): boolean => {
      let depth = 0;
      for (const ch of text.slice(fromIdx, toIdx)) {
        if (ch === '(') depth += 1;
        else if (ch === ')') {
          depth -= 1;
          if (depth < 0) return false;
        }
      }
      return depth === 0;
    };
    const enclosingParenBounds = (sepStart: number, sepEnd: number): { open: number; close: number } => {
      let open = contextStart;
      let d = 0;
      for (let i = sepStart - 1; i >= contextStart; i -= 1) {
        if (text[i] === ')') d += 1;
        else if (text[i] === '(') {
          if (d === 0) {
            open = i + 1;
            break;
          }
          d -= 1;
        }
      }
      let close = contextEnd;
      d = 0;
      for (let i = sepEnd; i < contextEnd; i += 1) {
        if (text[i] === '(') d += 1;
        else if (text[i] === ')') {
          if (d === 0) {
            close = i;
            break;
          }
          d -= 1;
        }
      }
      return { open, close };
    };
    for (const pattern of [
      /;/g,
      /\(\s*(?:and|or|while|whereas)\b|\b(?:and|or|while|whereas|over|versus|vs\.?|down\s+to|up\s+to)\b|(?<=\))\s+(?!(?:for|in|at|under|during)\s+phase\s*#?\s*\d+\b)|[,;:\u2014\u2013]/gi,
    ]) {
      const passStart = contextStart;
      const passEnd = contextEnd;
      for (const separator of text.slice(passStart, passEnd).matchAll(pattern)) {
        const separatorStart = passStart + separator.index;
        const separatorEnd = separatorStart + separator[0].length;
        const prefixSlice = text.slice(passStart, separatorStart);
        let parenDepth = 0;
        for (const ch of prefixSlice) {
          if (ch === '(') parenDepth += 1;
          else if (ch === ')' && parenDepth > 0) parenDepth -= 1;
        }
        const isTopLevel = parenDepth === 0 || separator[0].startsWith(')');
        if (!isTopLevel) {
          const innerBounds = enclosingParenBounds(separatorStart, separatorEnd);
          const innerLeft = text.slice(innerBounds.open, separatorStart);
          const innerRight = text.slice(separatorEnd, innerBounds.close);
          const leftIsLoadOnly =
            !/\b(?:MGD|(?:million\s+)?(?:gallons?|gal)\s*(?:\/|per\s+)(?:day|d|year|yr|hour|hr))\b/i.test(innerLeft) &&
            (/\b\d[\d,.]*\s*MW\b/i.test(innerLeft) || /\bphase\s*#?\s*\d+\b/i.test(innerLeft));
          const validInnerSplit =
            hasPair(text.slice(contextStart, separatorStart)) &&
            hasPair(innerRight) &&
            (hasPair(innerLeft) || leftIsLoadOnly);
          if (!validInnerSplit) continue;
        } else {
          if (!hasPair(text.slice(contextStart, separatorStart)) || !hasPair(text.slice(separatorEnd, contextEnd))) continue;
        }
        if (separatorEnd <= start) contextStart = separatorEnd;
        else if (separatorStart >= end) { contextEnd = separatorStart; break; }
      }
    }
  }
  return { start: contextStart, end: contextEnd };
}

function quantityContext(text: string, start: number, end: number, options: {
  isolateComparison?: boolean;
  isolatePhaseLoads?: boolean;
  isolateStatistics?: boolean;
} = {}): string {
  const bounds = quantityContextBounds(text, start, end, options);
  return text.slice(bounds.start, bounds.end).replace(/\s+/g, ' ').trim();
}

function parseNumber(raw: string): number {
  return Number(raw.replace(/,/g, ''));
}

function quantityFromMatch(
  text: string,
  match: RegExpExecArray,
  period: 'hour' | 'day' | 'year',
  multiplier: number,
): ParsedWaterQuantity | null {
  const lowRaw = match[1];
  if (!lowRaw) return null;
  const highRaw = match[2] || undefined;
  const lowNative = parseNumber(lowRaw);
  const highNative = highRaw ? parseNumber(highRaw) : lowNative;
  if (!Number.isFinite(lowNative) || !Number.isFinite(highNative)) return null;
  return {
    raw: match[0],
    start: match.index,
    end: match.index + match[0].length,
    excerpt: compactExcerpt(text, match.index, match.index + match[0].length),
    context: quantityContext(text, match.index, match.index + match[0].length),
    providerContext: quantityContext(text, match.index, match.index + match[0].length, { isolateComparison: true }),
    loadContext: quantityContext(text, match.index, match.index + match[0].length, { isolatePhaseLoads: true }),
    period,
    multiplier,
    lowRaw,
    ...(highRaw ? { highRaw } : {}),
    low: Math.min(lowNative, highNative) * multiplier,
    high: Math.max(lowNative, highNative) * multiplier,
  };
}

function parseWaterQuantities(text: string): ParsedWaterQuantity[] {
  const out: ParsedWaterQuantity[] = [];
  const occupied: Array<{ start: number; end: number }> = [];
  const overlaps = (start: number, end: number): boolean => occupied.some(
    (span) => start < span.end && end > span.start,
  );
  const collect = (
    pattern: RegExp,
    resolve: (match: RegExpExecArray) => { period: 'hour' | 'day' | 'year'; multiplier: number },
  ): void => {
    pattern.lastIndex = 0;
    let match: RegExpExecArray | null;
    while ((match = pattern.exec(text)) != null) {
      const start = match.index;
      const end = start + match[0].length;
      if (overlaps(start, end)) continue;
      const quantity = quantityFromMatch(text, match, resolve(match).period, resolve(match).multiplier);
      if (!quantity) continue;
      occupied.push({ start, end });
      out.push(quantity);
    }
  };

  collect(MGD_PATTERN, () => ({ period: 'day', multiplier: 1_000_000 }));
  collect(GPD_PATTERN, () => ({ period: 'day', multiplier: 1 }));
  collect(GALLON_RATE_PATTERN, (match) => {
    const unit = match[4] ?? '';
    return {
      period: /hour|hourly|hr|h/i.test(unit)
        ? 'hour'
        : /year|yr|annum/i.test(unit)
          ? 'year'
          : 'day',
      multiplier: match[3] ? 1_000_000 : 1,
    };
  });
  return out.sort((left, right) => text.indexOf(left.raw) - text.indexOf(right.raw));
}

/** Only a flow label attached to this rate, never another rate in the paragraph. */
function explicitWaterFlow(text: string, quantity: ParsedWaterQuantity): 'withdrawal' | 'consumption' | null {
  const before = text.slice(Math.max(0, quantity.start - 140), quantity.start);
  const label = [...before.matchAll(/(?<!high[- ]|low[- ])\b(withdrawals?|withdraw|consumption|consumptive\s+use|consume|evaporation|makeup(?:\s+withdrawal)?)\b/gi)].at(-1);
  const tail = label ? before.slice(label.index! + label[0].length) : '';
  const cleanTail = tail.replace(/\([^()\d]*\)/g, ' ');
  if (label && /^[^.,;(|\n\d]{0,65}$/.test(cleanTail) && !/\b(?:and|or|versus|vs)\b/i.test(cleanTail)) {
    const prefix = before.slice(0, label.index);
    if (/\b(?:not|neither|without)\s+(?:(?:a|specified|confirmed)\s+)*$/i.test(prefix)
      || /\b(?:cannot|can not|must not|should not|not)\s+(?:be\s+)?(?:classified|described|labelled|labeled|treated)\s+as\s*$/i.test(prefix)
      || /^\s+(?:basis\s+)?(?:is\s+)?not\s+specified\b/i.test(tail)
      || /\b(?:unknown|unspecified|unconfirmed|not\s+(?:known|established))\b/i.test(tail)) return null;
    return /withdraw|makeup/i.test(label[1]!) ? 'withdrawal' : 'consumption';
  }
  const after = text.slice(quantity.end, quantity.end + 90);
  if (/^\s+(?:withdrawal[- /]+(?:(?:versus|vs)[- /]+)?consumption|consumption[- /]+(?:(?:versus|vs)[- /]+)?withdrawal)\s+(?:basis\s+)?(?:is\s+)?(?:unspecified|unknown)\b/i.test(after)) return null;
  const following = /^\s+(?:of\s+)?(?:(?:modeled|screening|average[- ]day|annual[- ]average|average|day|daily|wet[- ]tower|wet|evaporative|evaporation|adiabatic|for)\s+)*(withdrawals?|consumption|consumptive\s+use|evaporation|makeup)\b/i.exec(after);
  return following ? /withdraw|makeup/i.test(following[1]!) ? 'withdrawal' : 'consumption' : null;
}

/** A rate can be labeled withdrawal and then incorrectly equated with
 * consumptive use in the same predicate. General watershed impacts and a
 * separate consumption quantity do not make that equivalence. */
function quantityIsCalledConsumptiveUse(text: string, quantity: ParsedWaterQuantity): boolean {
  const before = text.slice(Math.max(0, quantity.start - 220), quantity.start)
    .split(/[.!?;|\n]|\b(?:but|however)\b/i).at(-1) ?? '';
  if (/\b(?:(?:incorrect|false|untrue)\s+that|not\s+true\s+that|(?:does|do)\s+not\s+mean\s+that)\b/i.test(before)) return false;
  const after = text.slice(quantity.end, quantity.end + 220);
  return /^\s*(?:withdrawals?\b)?\s*\)?\s*,?\s*(?:is\s+not\s+withdrawal\s+but\s+)?(?:(?:is|would\s+be)\s+(?:substantial|significant|large|high)\s+and\s+)?(?:represents?|would\s+represent|is|would\s+be)\s+(?:a\s+)?(?:(?:significant|substantial|new|large|additional|modeled|modelled|screening)\s+){0,3}consumptive\s+use\b/i.test(after);
}

function combinedWaterTitleFindings(report: SiteRagReport, bands: AllowedBand[]): WaterUseScreeningFinding[] {
  return (report.structuredFindings ?? []).flatMap(row => {
    const title = row.title ?? '';
    const combined = /\b(?:BTM\s+(?:power|generation)|power[- ]plant|power\s+generation)\s*(?:and|&|\+)\s*(?:data[- ]cent(?:er|re)\s+)?cooling\b/i.test(title)
      || /\b(?:data[- ]cent(?:er|re)\s+)?cooling\s*(?:and|&|\+)\s*(?:BTM\s+(?:power|generation)|power[- ]plant|power\s+generation)\b/i.test(title);
    if (!combined || !/\bwater\b/i.test(title) || /\b(?:separat(?:e|ely)|distinct|compar(?:e|ison))\b/i.test(title)) return [];
    return parseWaterQuantities(row.detail ?? '').flatMap(quantity => {
      const matching = bands.filter(band => matchesBand(quantity, band));
      if (!matching.length || matching.some(band => band.measure === 'generation-duty')) return [];
      return [finding('water-end-use-mislabeled', quantity.raw, `${title}: ${row.detail}`,
        'Label this finding as modeled data-center water only. A combined BTM power-and-cooling demand requires its own separately supported generation-duty basis; the data-center screen does not size power-plant process water.',
        `The finding title assigns the data-center screening value ${quantity.raw} to combined BTM power and cooling.`)];
    });
  });
}

function consumptivePercentageRanges(text: string): Array<{ low: number; high: number }> {
  const out: Array<{ low: number; high: number }> = [];
  const pattern = new RegExp(CONSUMPTIVE_PERCENT_PATTERN.source, 'gi');
  let match: RegExpExecArray | null;
  while ((match = pattern.exec(text)) != null) {
    const low = parseNumber(match[1] ?? '');
    const high = parseNumber(match[2] || match[1] || '');
    if (!Number.isFinite(low) || !Number.isFinite(high)) continue;
    out.push({ low: Math.min(low, high), high: Math.max(low, high) });
  }
  return out;
}

function documentedConsumptivePercentages(report: SiteRagReport): AllowedConsumptivePercentage[] {
  const claims = report.siteContextSnapshot?.claims ?? [];
  const out = new Map<string, AllowedConsumptivePercentage>();
  for (const claim of claims) {
    if (claim.category !== 'water.cooling'
      || claim.source !== 'document-extracted'
      || !claim.sourceDocId?.trim()) continue;
    const texts = [claim.claimedValue, claim.claim, claim.sourceSpan]
      .filter((value): value is string | number => typeof value === 'string' || typeof value === 'number')
      .map(String);
    for (const text of texts) {
      for (const range of consumptivePercentageRanges(text)) {
        const supported = { ...range, sourceDocId: claim.sourceDocId };
        out.set(`${supported.low}:${supported.high}:${supported.sourceDocId}`, supported);
      }
    }
  }
  return [...out.values()];
}

function bandsFromScreen(screen: WaterUseScreeningResult): AllowedBand[] {
  return screen.scenarios.flatMap((scenario: WaterUseScenarioResult) => {
    const bands: AllowedBand[] = [{
      scenarioId: scenario.scenarioId,
      label: scenario.label,
      period: 'day' as const,
      low: scenario.averageGallonsPerDay.low,
      high: scenario.averageGallonsPerDay.high,
      boundary: scenario.boundary,
      flowBasis: scenario.flowBasis,
      measure: 'scenario-average',
    },
    {
      scenarioId: scenario.scenarioId,
      label: scenario.label,
      period: 'year' as const,
      low: scenario.annualGallons.low,
      high: scenario.annualGallons.high,
      boundary: scenario.boundary,
      flowBasis: scenario.flowBasis,
      measure: 'scenario-annual',
    }];

    if (scenario.fullLoadGallonsPerDay) {
      bands.push({
        scenarioId: `${scenario.scenarioId}:full-load`,
        label: `${scenario.label} · 100%-load cooling sensitivity`,
        period: 'day',
        low: scenario.fullLoadGallonsPerDay.low,
        high: scenario.fullLoadGallonsPerDay.high,
        boundary: scenario.boundary,
        flowBasis: scenario.flowBasis,
        measure: 'scenario-full-load',
      });
    }
    const adiabatic = scenario.adiabaticWaterScreening;
    if (adiabatic?.status === 'calculated') {
      bands.push(
        {
          scenarioId: `${scenario.scenarioId}:adiabatic-evaporation`,
          label: `${scenario.label} · adiabatic evaporation`,
          period: 'day',
          low: adiabatic.averageDailyEvaporationGallons.low,
          high: adiabatic.averageDailyEvaporationGallons.high,
          boundary: 'cooling-process-only',
          flowBasis: 'consumption',
          measure: 'adiabatic-evaporation',
        },
        {
          scenarioId: `${scenario.scenarioId}:adiabatic-evaporation`,
          label: `${scenario.label} · annual adiabatic evaporation`,
          period: 'year',
          low: adiabatic.annualEvaporationGallons.low,
          high: adiabatic.annualEvaporationGallons.high,
          boundary: 'cooling-process-only',
          flowBasis: 'consumption',
          measure: 'adiabatic-evaporation',
        },
        {
          scenarioId: `${scenario.scenarioId}:adiabatic-evaporation-peak-day`,
          label: `${scenario.label} · peak TMY-day adiabatic evaporation`,
          period: 'day',
          low: adiabatic.peakDailyEvaporationGallons.low,
          high: adiabatic.peakDailyEvaporationGallons.high,
          boundary: 'cooling-process-only',
          flowBasis: 'consumption',
          measure: 'adiabatic-evaporation',
        },
        {
          scenarioId: `${scenario.scenarioId}:adiabatic-makeup`,
          label: `${scenario.label} · adiabatic makeup`,
          period: 'day',
          low: adiabatic.averageDailyMakeupGallons.low,
          high: adiabatic.averageDailyMakeupGallons.high,
          boundary: 'cooling-process-only',
          flowBasis: 'withdrawal',
          measure: 'adiabatic-makeup',
        },
        {
          scenarioId: `${scenario.scenarioId}:adiabatic-makeup-peak-day`,
          label: `${scenario.label} · peak TMY-day adiabatic makeup`,
          period: 'day',
          low: adiabatic.peakDailyMakeupGallons.low,
          high: adiabatic.peakDailyMakeupGallons.high,
          boundary: 'cooling-process-only',
          flowBasis: 'withdrawal',
          measure: 'adiabatic-makeup',
        },
        {
          scenarioId: `${scenario.scenarioId}:adiabatic-makeup`,
          label: `${scenario.label} · annual adiabatic makeup`,
          period: 'year',
          low: adiabatic.annualMakeupGallons.low,
          high: adiabatic.annualMakeupGallons.high,
          boundary: 'cooling-process-only',
          flowBasis: 'withdrawal',
          measure: 'adiabatic-makeup',
        },
        {
          scenarioId: `${scenario.scenarioId}:adiabatic-evaporation-peak`,
          label: `${scenario.label} · peak active-hour adiabatic evaporation`,
          period: 'hour',
          low: adiabatic.peakActiveHourEvaporationGallonsPerHour.low,
          high: adiabatic.peakActiveHourEvaporationGallonsPerHour.high,
          boundary: 'cooling-process-only',
          flowBasis: 'consumption',
          measure: 'adiabatic-evaporation',
        },
        {
          scenarioId: `${scenario.scenarioId}:adiabatic-makeup-peak`,
          label: `${scenario.label} · peak active-hour adiabatic makeup`,
          period: 'hour',
          low: adiabatic.peakActiveHourMakeupGallonsPerHour.low,
          high: adiabatic.peakActiveHourMakeupGallonsPerHour.high,
          boundary: 'cooling-process-only',
          flowBasis: 'withdrawal',
          measure: 'adiabatic-makeup',
        },
      );
    }
    return bands;
  });
}

/**
 * P3.3 — the frozen promoted-scenario water duty (`generationImpactProjection
 * .waterResource`) is deterministic solver output the report renders as a
 * table. Now that `extractSections` makes it reader-visible text, its ten
 * figures are allowed values — exact (low = high), daily — so the table and a
 * caveated restatement of it pass, while any other gal/day figure still needs
 * a screen band or an attributed comparable. These are NOT screen bands: they
 * never satisfy the Water Resource "reproduce the SED screen" disclosure.
 */
function bandsFromGenerationProjection(
  report: SiteRagReport,
  ledger: readonly WholeSiteWaterLedgerEntry[],
): AllowedBand[] {
  return [
    ...bandsFromPromotedWaterDuty(report, ledger),
    ...bandsFromScreenedConfigurations(report),
  ];
}

/**
 * P3.3 follow-through — the "Configurations screened before promotion" table
 * prints a peak water consumption per compared configuration. The audit text
 * now renders it (labelled a screened-configuration duty), so the guard admits
 * each exact figure the same way it admits the promoted duty table.
 */
function bandsFromScreenedConfigurations(report: SiteRagReport): AllowedBand[] {
  const rows = report.generationImpactProjection?.comparison?.rows ?? [];
  return rows.flatMap((row) => (
    finiteNonNegative(row.peakWaterConsumptionGalPerDay)
      ? [{
        scenarioId: `screened-configuration:${row.scenarioId}`,
        label: `Screened configuration · ${row.scenarioName}`,
        period: 'day' as const,
        low: row.peakWaterConsumptionGalPerDay,
        high: row.peakWaterConsumptionGalPerDay,
        boundary: 'campus' as WaterUseScenarioResult['boundary'],
        flowBasis: 'consumption' as const,
        measure: 'generation-duty' as const,
      }]
      : []
  ));
}

const PROMOTED_WATER_DUTY_SCENARIO_ID = 'promoted-scenario:water-duty';

function bandsFromPromotedWaterDuty(
  report: SiteRagReport,
  ledger: readonly WholeSiteWaterLedgerEntry[],
): AllowedBand[] {
  const water = report.generationImpactProjection?.waterResource;
  if (!water) return [];
  // W6 — a declared "available capacity" equal to a whole-site source quantity
  // (an allocation, demand or margin) is not a consumptive-use capacity, and
  // the headroom subtracted from it is not a consumption headroom. The
  // projection may still print both rows; authored prose may not restate them.
  // Lane A's document role authority decides first: a frozen document that
  // binds the input as a consumptive-use limit keeps it restatable even at a
  // ledger value, and one that does not bind it never makes it restatable.
  const available = water.availableConsumptionGalPerDay;
  const provenance = finiteNonNegative(available) ? declaredInputProvenance(report, available) : 'no-document';
  const declaredInputIsWholeSite = finiteNonNegative(available) && available > 0
    && provenance !== 'documented-consumptive-limit'
    && (provenance === 'document-unbound' || matchWholeSiteWaterRoles(ledger, available).length > 0);
  const rows: Array<[string, number | null, WaterUseScenarioResult['flowBasis'], NonNullable<AllowedBand['dutyComponent']>]> = [
    ['Generation withdrawal', water.generationWithdrawalGalPerDay, 'withdrawal', 'generation'],
    ['Generation consumption', water.generationConsumptionGalPerDay, 'consumption', 'generation'],
    ['CCS withdrawal', water.ccsWithdrawalGalPerDay, 'withdrawal', 'ccs'],
    ['CCS consumption', water.ccsConsumptionGalPerDay, 'consumption', 'ccs'],
    ['Data-center withdrawal', water.dataCenterWithdrawalGalPerDay, 'withdrawal', 'data-center'],
    ['Data-center consumption', water.dataCenterConsumptionGalPerDay, 'consumption', 'data-center'],
    ['Total withdrawal', water.totalWithdrawalGalPerDay, 'withdrawal', 'total'],
    ['Total consumption', water.totalConsumptionGalPerDay, 'consumption', 'total'],
    ['Documented available capacity', water.availableConsumptionGalPerDay, 'consumption', 'declared-input'],
    ['Consumption headroom', water.consumptionHeadroomGalPerDay, 'consumption', 'declared-input'],
  ];
  return rows.flatMap(([label, value, flowBasis, dutyComponent]) => (
    finiteNonNegative(value)
      ? [{
        scenarioId: PROMOTED_WATER_DUTY_SCENARIO_ID,
        label: `Promoted scenario · ${label}`,
        period: 'day' as const,
        low: value,
        high: value,
        boundary: 'campus' as WaterUseScenarioResult['boundary'],
        flowBasis,
        measure: 'generation-duty' as const,
        dutyComponent,
        ...(declaredInputIsWholeSite && dutyComponent === 'declared-input' ? { projectionTextOnly: true } : {}),
      }]
      : []
  ));
}

/**
 * W6 — lane A's document role authority for the report's frozen declared
 * available-consumption input at this daily rate (read from the frozen
 * scenario, never live documents). 'no-document' for any other rate or when
 * the report froze no scenario: the ledger comparison then decides.
 */
function declaredInputProvenance(report: SiteRagReport, galPerDay: number): DeclaredWaterConsumptionProvenance {
  const input = report.generationImpactScenarioAtGen?.input;
  const declared = input?.availableWaterConsumptionGalPerDay;
  if (declared == null || !Number.isFinite(declared)
    || Math.abs(declared - galPerDay) > DEFAULT_WHOLE_SITE_WATER_TOLERANCE_GAL_PER_DAY) return 'no-document';
  return declaredWaterConsumptionProvenance(input, declared);
}

/**
 * W6 — the exact span of the deterministic Generation & Impact projection
 * block inside the audit text. Its rows are the frozen projection itself (W7
 * caveats their roles in the basis statement); authored prose is everything
 * else. Null when the projection is absent or the text is legacy markdown.
 */
function deterministicProjectionSpan(text: string, report: SiteRagReport): { start: number; end: number } | null {
  const rendered = renderGenerationImpactProjection(report.generationImpactProjection);
  if (!rendered.trim()) return null;
  const section = `# generationImpactProjection\n${rendered}`;
  const start = text.indexOf(section);
  return start < 0 ? null : { start, end: start + section.length };
}

function insideSpan(quantity: ParsedWaterQuantity, span: { start: number; end: number } | null): boolean {
  return span != null && quantity.start >= span.start && quantity.end <= span.end;
}

function comparablePoints(sed: SiteEvidenceDocument): AllowedComparablePoint[] {
  const records = sed.economicEvidence?.data?.research?.policyValidRecords ?? [];
  const out: AllowedComparablePoint[] = [];
  for (const record of records) {
    for (const observation of record.waterTimeline ?? []) {
      const value = observation.value;
      if (finiteNonNegative(value.normalizedGallonsPerDay)) {
        out.push({
          projectId: record.projectId,
          facilityName: record.facilityName,
          value: value.normalizedGallonsPerDay,
          period: 'day',
          stage: value.stage,
        });
        continue;
      }
      if (!finiteNonNegative(value.amount)) continue;
      if (value.units === 'gal/day') {
        out.push({ projectId: record.projectId, facilityName: record.facilityName, value: value.amount, period: 'day', stage: value.stage });
      } else if (value.units === 'million-gal/day') {
        out.push({ projectId: record.projectId, facilityName: record.facilityName, value: value.amount * 1_000_000, period: 'day', stage: value.stage });
      } else if (value.units === 'liters/day') {
        out.push({ projectId: record.projectId, facilityName: record.facilityName, value: value.amount / 3.785411784, period: 'day', stage: value.stage });
      } else if (value.units === 'acre-ft/year') {
        out.push({ projectId: record.projectId, facilityName: record.facilityName, value: value.amount * 325_851, period: 'year', stage: value.stage });
      }
    }
  }
  return out;
}

function decimalPlaces(raw: string): number {
  const dot = raw.indexOf('.');
  return dot < 0 ? 0 : raw.length - dot - 1;
}

/**
 * Compare against the way the SED prompt renderer displays values: whole gpd
 * below one million and two-decimal MGD above it. One-decimal MGD is accepted
 * only when it is the honest rounded form of an endpoint; arbitrary interior
 * points remain invalid.
 */
function endpointMatches(observed: number, raw: string, multiplier: number, expected: number): boolean {
  if (!Number.isFinite(expected)) return false;
  const nativeExpected = expected / multiplier;
  const digits = decimalPlaces(raw.replace(/,/g, ''));
  if (multiplier === 1_000_000) {
    if (digits === 0 && expected !== 0) return false;
    return Number(nativeExpected.toFixed(digits)) === Number(raw.replace(/,/g, ''));
  }
  if (digits === 0) return Math.round(nativeExpected) === Number(raw.replace(/,/g, ''));
  return Number(nativeExpected.toFixed(digits)) === Number(raw.replace(/,/g, ''));
}


/** Source-stated site rates are neither utility-system production nor modeled
 * demand endpoints. Reuse the exact current ledger; a report number alone or
 * another role with the same value can never create an admissible input. */
const SOURCE_WATER_ROLES = [
  'siteWaterAllocationMgd', 'projectAverageWaterDemandMgd', 'projectPeakWaterDemandMgd',
  'projectWaterMarginMgd', 'siteWastewaterAllocationMgd',
  'projectPeakWastewaterDischargeMgd', 'projectWastewaterMarginMgd',
] as const satisfies readonly SiteResourceKey[];
type SourceWaterRole = typeof SOURCE_WATER_ROLES[number];
const SOURCE_WATER_LABELS: Readonly<Record<SourceWaterRole, RegExp>> = {
  siteWaterAllocationMgd: /\b(?<!wastewater(?:\s+service)?\s+)(?<!wastewater(?:\s+allocation)?\s+margin\s*\(\s*)(?:(?:site|municipal|water|potable(?:\s*\/\s*process)?)\s+)*(?:service\s+)?(?:allocation|allotment)\b(?!\s*(?:-\s*minus\s*-|-|minus)\s*peak\b)|\ballocated\s+(?:potable(?:\s*\/\s*process)?|municipal|water)(?:\s+supply)?\b/gi,
  projectAverageWaterDemandMgd: /\b(?:average|avg)(?:\s+total)?(?:\s+project)?(?:\s+water)?\s+demand\b/gi,
  projectPeakWaterDemandMgd: /\bpeak(?:\s+total)?(?:\s+project)?(?:\s+water)?\s+demand\b|\bproject\s+peak(?:\s+water)?\s+demand\b/gi,
  projectWaterMarginMgd: /\b(?:project\s+)?water(?:\s+allocation(?:\s*(?:-\s*minus\s*-|-|minus)\s*peak)?)?\s+margin\b|\ballocation\s+minus\s+peak\s+(?:water\s+)?demand\b|(?<=(?:peak\s+(?:project\s+)?|project\s+peak\s+)(?:water\s+)?demand\s+of\s+\d[\d,.]*\s*(?:MGD|gpd|(?:US\s+)?(?:gallons?|gal)\s*(?:\/|per\s+)day)\s*,?\s+and\s+(?:source-stated\s+)?(?:planning\s+)?)margin\b/gi,
  siteWastewaterAllocationMgd: /\b(?:site\s+)?wastewater(?:\s+service)?\s+(?:allocation|allotment|availability)\b(?!\s*(?:-\s*minus\s*-|-|minus)\s*peak\b)|(?<=wastewater(?:\s+allocation)?\s+margin\s*\(\s*)(?:site\s+)?(?:service\s+)?(?:allocation|allotment)\b/gi,
  projectPeakWastewaterDischargeMgd: /\bpeak(?:\s+project)?(?:\s+wastewater)?\s+discharge\b|\bproject\s+peak(?:\s+wastewater)?\s+discharge\b/gi,
  projectWastewaterMarginMgd: /\b(?:project\s+)?wastewater(?:\s+allocation(?:\s*(?:-\s*minus\s*-|-|minus)\s*peak)?)?\s+margin\b|(?<=(?:peak\s+(?:project\s+)?|project\s+peak\s+)(?:wastewater\s+)?discharge\s+of\s+\d[\d,.]*\s*(?:MGD|gpd|(?:US\s+)?(?:gallons?|gal)\s*(?:\/|per\s+)day)\s*,?\s+and\s+(?:source-stated\s+)?(?:planning\s+)?)margin\b/gi,
};

function stripParentheticalFormula(slice: string): string {
  return slice.replace(/^\s*\([^()]*\bminus\b[^()]*\)/i, '');
}

function stripLeadingEquivalentDailyRate(slice: string, quantity: ParsedWaterQuantity): string {
  const withoutFormula = stripParentheticalFormula(slice);
  if (quantity.period !== 'day' || quantity.highRaw) return withoutFormula;
  const eq = /^([\s(),:*_-]*(?:(?:of|for|is|equals|at)\s+)?)[\s(/=-]*(\d[\d,]*(?:\.\d+)?)\s*(MGD|Mgal\s*\/\s*d|(?:million\s+)?(?:US\s+)?(?:gallons?|gal)\s*(?:\/|per\s+)(?:day|daily|d)|gpd)\b[\s)/=-]*/i.exec(withoutFormula);
  if (!eq) return withoutFormula;
  const otherVal = parseNumber(eq[2]!) * (/^(?:MGD|Mgal|million)/i.test(eq[3]!) ? 1_000_000 : 1);
  if (!Number.isFinite(otherVal) || Math.abs(otherVal - quantity.low) > 1e-3) return withoutFormula;
  return (eq[1] ?? '') + withoutFormula.slice(eq[0].length);
}

function sourceWaterRole(text: string, quantity: ParsedWaterQuantity): SourceWaterRole | undefined {
  if (quantity.highRaw) return undefined;
  // Select the nearest role label before this value, never a role attached to
  // a following value. Sentence/record boundaries cannot lend an actor.
  const prefix = text.slice(Math.max(0, quantity.start - 260), quantity.start)
    .split(/\n|\s+\|\s+|;|[.!?](?=\s|$)/).at(-1) ?? '';
  const matches = SOURCE_WATER_ROLES.flatMap(key => [...prefix.matchAll(SOURCE_WATER_LABELS[key])]
    .filter(match => {
      const gap = stripLeadingEquivalentDailyRate(prefix.slice(match.index! + match[0].length), quantity);
      if (/[,;]|\b(?:and|or|while|whereas|versus|vs\.?|against|minus|less|(?:calculated|computed|defined)\s+as)\b/i.test(gap)) return false;
      // A role attached to an earlier rate cannot own this one. The only
      // numeric carry is an explicit native-unit equivalence (gpd = MGD).
      return !/\b(?:MGD|(?:US\s+)?(?:gallons?|gal)\s*(?:\/|per\s+)(?:day|hour|year)|gpd)\b/i.test(gap)
        || /\b(?:MGD|(?:US\s+)?(?:gallons?|gal)\s*(?:\/|per\s+)day|gpd)\s*=\s*$/i.test(gap);
    })
    .map(match => ({ key, start: match.index!, end: match.index! + match[0].length })));
  matches.sort((left, right) => right.end - left.end || left.start - right.start);
  let role = matches[0]?.key;
  if (!role) {
    // Ordinary prose also places the identity immediately after a rate:
    // "75,000 US gallons/day allocation". A later clause cannot lend a role.
    const suffix = text.slice(quantity.end, quantity.end + 120)
      .split(/\n|\s+\|\s+|;|[.!?](?=\s|$)/)[0] ?? '';
    const following = SOURCE_WATER_ROLES.flatMap(key => [...suffix.matchAll(SOURCE_WATER_LABELS[key])]
      // These qualifiers describe this rate; arbitrary prose, another actor,
      // another quantity or a clause connector cannot lend a following role.
      // A status adjective ("0.075 MGD firm water service allocation") also
      // describes this rate: it must bind the role so the status check below
      // sees it. Without a calculated screen (or for a source-role-only type)
      // nothing else would audit that sentence.
      .filter(match => /^[\s(),:*_-]*(?:(?:of|for)\s+)?(?:(?:source-stated|daily|planning|actual|measured|metered|verified|permitted|authorized|approved|committed|guaranteed|firm)\s+)*$/i.test(stripLeadingEquivalentDailyRate(suffix.slice(0, match.index), quantity)))
      .map(match => ({ key, start: match.index!, end: match.index! + match[0].length })));
    following.sort((left, right) => left.start - right.start || right.end - left.end);
    role = following[0]?.key;
    if (role === 'siteWaterAllocationMgd' && following[0]) {
      const matchedLabel = suffix.slice(following[0].start, following[0].end);
      const lastWastewater = [...prefix.matchAll(/\bwastewater\b/gi)].at(-1)?.index ?? -1;
      const lastPotable = [...prefix.matchAll(/\b(?:potable|municipal\s+water|water\s+(?:service|allocation|allotment|supply))\b/gi)].at(-1)?.index ?? -1;
      if (!/\b(?:water|potable|municipal)\b/i.test(matchedLabel) && lastWastewater > lastPotable) {
        role = 'siteWastewaterAllocationMgd';
      }
    }
  }
  if (!role) return undefined;
  // Equal-ended labels choose the complete subject, so a wastewater label
  // is not replaced by its shorter generic allocation/margin suffix.
  // A different actor mentioned earlier in the sentence lends no authority.
  return role;
}

function sourceWaterRows(sed: SiteEvidenceDocument | null | undefined): SiteResourceSourceRow[] {
  const view = siteResourceViewFromSed(sed);
  if (!view) return [];
  return siteResourceSourceRowsFromSed(sed).filter(row => {
    if (!SOURCE_WATER_ROLES.includes(row.key as SourceWaterRole) || typeof row.value !== 'number'
      || !view.facts.some(fact => fact.key === row.key && fact.value === row.value)) return false;
    const review = sed?.waterResource?.provenanceDetail?.fieldReviews?.[row.key];
    return !review || review.decision === 'accepted';
  });
}

/** The source-stated role audit of one quantity: the role it binds and its findings. */
function sourceWaterStatement(
  text: string, quantity: ParsedWaterQuantity, report: SiteRagReport,
  rows: readonly SiteResourceSourceRow[], sed?: SiteEvidenceDocument | null,
): { role: SourceWaterRole; findings: WaterUseScreeningFinding[] } | undefined {
  const role = sourceWaterRole(text, quantity);
  if (!role) return undefined;
  const findings = sourceWaterStatementFindings(text, quantity, report, rows, role, sed);
  return findings === undefined ? undefined : { role, findings };
}

function sourceWaterStatementFindings(
  text: string, quantity: ParsedWaterQuantity, report: SiteRagReport,
  rows: readonly SiteResourceSourceRow[], role: SourceWaterRole, sed?: SiteEvidenceDocument | null,
): WaterUseScreeningFinding[] | undefined {
  const context = quantityContext(text, quantity.start, quantity.end, { isolateStatistics: true, isolateComparison: true });
  const cited = sameContextReferenceIndices(context).map(index => report.references?.[index - 1])
    .filter((reference): reference is DataSource => !!reference && reference.field === 'documents'
      && !!reference.documentId && reference.url === `doc://${reference.documentId}`);
  const demandRole = role === 'projectAverageWaterDemandMgd' || role === 'projectPeakWaterDemandMgd';
  // Ordinary modeled-demand prose still follows the unchanged model checker.
  // Allocation, discharge and margins have no model-demand interpretation.
  if (demandRole && !cited.length && MODELED_CAVEAT_PATTERN.test(context)) return undefined;
  const mgd = quantity.multiplier === 1_000_000 ? parseNumber(quantity.lowRaw) : parseNumber(quantity.lowRaw) / 1_000_000;
  const candidates = rows.filter(row => row.key === role && row.value === mgd && quantity.period === 'day' && !quantity.highRaw);
  const isCompanionDoc = (docId: string | undefined): boolean => {
    if (!docId || !candidates.length) return false;
    if (rows.some(r => r.fact.documentId === docId && (r.key !== role || r.value !== mgd))) return false;
    const uploaded = sed?.uploadedDocuments?.find(d => d.docId === docId);
    if (!uploaded) return false;
    const rawText = [uploaded.textContent ?? '', ...(uploaded.pages ?? []).map(p => p.text ?? '')].join('\n');
    if (!/\b(?:whole[- ]site\s+water\s+facts\s+retained|retain(?:ed)?\s+the\s+original\s+[\w,]+\s*(?:gpd|MGD)?\s*whole[- ]site\s+allocation)\b/i.test(rawText)) return false;
    const hasPrimaryInContext = cited.some(ref => candidates.some(row => row.fact.documentId === ref.documentId));
    const gpdFormatted = Math.round(mgd * 1_000_000).toLocaleString('en-US');
    return hasPrimaryInContext || rawText.includes(gpdFormatted) || rawText.includes(String(mgd));
  };
  const matching = candidates.filter(row => cited.some(reference => reference.documentId === row.fact.documentId || isCompanionDoc(reference.documentId)));
  const expected = 'Quote the exact current source-stated daily quantity with its own allocation, average/peak demand, discharge or margin role and originating document reference. Source statements do not establish measured use, permit authority or firm utility service.';
  if (!matching.length || cited.some(reference => !matching.some(row => row.fact.documentId === reference.documentId) && !isCompanionDoc(reference.documentId))) {
    const findings = [finding('unsupported-site-water-value', quantity.raw, quantity.excerpt, expected,
      `The report's ${role} claim ${quantity.raw} does not match a current non-conflicting source row and same-context originating document citation for that role.`)];
    // A missing source row does not excuse a separate quantified claim of
    // approved/permitted authority. Retain the same status/negation policy as
    // the modeled-demand lane before this source-role branch returns.
    if (hasUnnegatedWaterStatusAssertion(maskNumberedRefTokens(quantity.context))) findings.push(finding(
      'modeled-water-mislabeled', quantity.raw, quantity.excerpt, expected,
      `The report assigns an unsupported actual, permitted, measured, requested, design or committed status to the ${role} quantity ${quantity.raw}.`,
    ));
    return findings;
  }
  const claimContext = maskNumberedRefTokens(context);
  const assurance = claimContext.split(/;|\b(?:and|but|however)\b/i).some(clause =>
    [...clause.matchAll(/\b(?:actual|measured|metered|verified|permitted|authorized|approved|committed|guaranteed|firm)\b/gi)]
      .some(match => !/\b(?:not|never|no|without|unconfirmed|unverified)\s+(?:(?:actual|measured|metered|verified|permitted|authorized|approved|committed|guaranteed|firm|independently|currently|yet|a|an|as|to\s+be|or|and)\s+){0,6}$/i.test(clause.slice(0, match.index))
        && !/\bnot\s+(?:established|treated|represented|claimed|described|confirmed|recorded)\s+as\s+$/i.test(clause.slice(0, match.index))
        // A source statement explicitly disclaims these statuses; this is
        // not an affirmative measured-use or firm-service assertion. Keep
        // the scope within the same clause and the stated status list.
        && !(!/\b(?:not\s+true|false|incorrect)\s+(?:that|to)\b/i.test(clause.slice(0, match.index))
          && /\b(?:do|does)\s+not\s+establish\s+(?:(?:actual|measured|metered|verified|permitted|authorized|approved|committed|guaranteed|firm|use|permit|authority|utility|service|or)\b[\s,-]*)*$/i.test(clause.slice(0, match.index)))
        && !/\brather\s+than\s+(?:(?:actual|measured|metered|verified|permitted|authorized|approved|committed|guaranteed|firm|a|an|the|utility|commitment|permit|volume|engineering|design|site|use|consumptive|water|rights|service|contracts?|feasible|minimum|buildable|capacity|or)\b[\s,/-]*)*$/i.test(clause.slice(0, match.index))));
  // Planning is an assumption label only in this quantity's own assertion;
  // another sentence, field or contrasting clause cannot qualify this rate.
  const bounds = quantityContextBounds(text, quantity.start, quantity.end,
    { isolateStatistics: true, isolateComparison: true });
  const clauseBoundary = /;|\b(?:and|but|however|while|whereas)\b/i;
  const planningClause = (text.slice(bounds.start, quantity.start).split(clauseBoundary).at(-1) ?? '')
    + quantity.raw + (text.slice(quantity.end, bounds.end).split(clauseBoundary)[0] ?? '');
  const planningQualified = [...planningClause.matchAll(/\bplanning\b/gi)].some(match =>
    !/\b(?:not|never|no|without)\s+(?:(?:a|an|the|source-stated)\s+){0,3}$/i.test(planningClause.slice(0, match.index)));
  const lacksAssumption = matching.some(row => row.assumed)
    && !/\b(?:assum(?:ed|ption|ptions)|unverified|scenario|estimate(?:d)?|fictional|synthetic|model(?:ed|led|\s+statements?))\b/i.test(claimContext)
    && !planningQualified;
  if (assurance || lacksAssumption) return [finding('modeled-water-mislabeled', quantity.raw, quantity.excerpt, expected,
    `The report promotes the source-stated ${role} quantity ${quantity.raw} beyond its recorded statement/assumption qualification.`)];
  return [];
}

// ---------------------------------------------------------------------------
// W6 — cross-role conflation of whole-site source quantities
// ---------------------------------------------------------------------------

const WHOLE_SITE_LEDGER_LABELS: Readonly<Record<WholeSiteWaterLedgerKey, string>> = {
  siteWaterAllocationMgd: 'site water allocation',
  projectAverageWaterDemandMgd: 'average project water demand',
  projectPeakWaterDemandMgd: 'peak project water demand',
  projectWaterMarginMgd: 'project water allocation margin',
};

/** Roles a whole-site allocation, demand or margin figure can never hold. */
const PROCESS_OR_LIMIT_ROLES: ReadonlySet<WaterQuantityRole> = new Set<WaterQuantityRole>([
  'cooling-consumption', 'withdrawal-makeup', 'consumptive-use-limit',
]);

/** An explicit disclaimer of the classified role itself: "…; it is not a consumptive-use limit". */
const ROLE_DISCLAIMER_LEAD = String.raw`(?:\b(?:not|neither|nor|rather\s+than|instead\s+of)|\b(?:(?:does|do|did|would|will|should|must|can|may)\s+not|cannot)\s+(?:be\s+)?(?:establish|represent|constitute|set|create|treated\s+as|read\s+as|used\s+as|presented\s+as|described\s+as))\s+(?:(?:a|an|the|any)\s+)?(?:[\w-]+\s+){0,2}?`;
const CLASSIFIED_ROLE_DISCLAIMER: Readonly<Partial<Record<WaterQuantityRole, RegExp>>> = {
  'consumptive-use-limit': new RegExp(String.raw`${ROLE_DISCLAIMER_LEAD}(?:consumptive[- ]use\s+(?:limit|capacity|cap|entitlement|right|allowance)s?|consumptive\s+(?:capacity|entitlement|right|limit|allowance)s?|limits?\s+on\s+consumptive\s+use)\b`, 'i'),
  'cooling-consumption': new RegExp(String.raw`${ROLE_DISCLAIMER_LEAD}cooling(?:[- ]process)?\s+consumption\b`, 'i'),
};
/** The head's role does not bind the rate: "… is not limited by the", "… is distinct from the", "Without a documented consumptive-use limit, the". */
const HEAD_LINK_NEGATED = /\b(?:(?:is|are|was|were|does|do|did)\s+not|(?:should|must)\s+not\s+be|cannot\s+be)\s+(?!(?:[\w-]+\s+){0,2}?(?:exceed|surpass|go|rise|separate|distinct|different|independent|apart|less|lower|below|under)\w*\b)(?:[\w-]+\s+){0,3}?(?:(?:the|a|an)\s+)?$|(?<!\bnot\s)\b(?:distinct|separate(?:ly)?|different|independent(?:ly)?|apart)\s+(?:from|of)\s+(?:the\s+)?$|\bcompare[sd]?\s+(?:[\w-]+\s+){0,3}?(?:with|to|against)\s+(?:the\s+)?$/i;
const HEAD_LEAD_ABSENT_LIMIT = /^\s*(?:without|absent|in\s+the\s+absence\s+of|lacking|with\s+no)\b[^,;]*\bconsumptive\b[^,;]*,\s*(?:(?:the|a|an)\s+)?$/i;
function classifiedRoleLinkNegated(maskedText: string, quantity: ParsedWaterQuantity): boolean {
  const head = maskedText.slice(Math.max(0, quantity.start - 300), quantity.start).split(SENTENCE_BREAK).at(-1) ?? '';
  return HEAD_LINK_NEGATED.test(head) || HEAD_LEAD_ABSENT_LIMIT.test(head);
}

/**
 * A consumptive-use limit asserted before the rate. The classifier reads
 * "consumptive use is limited to …" as consumption, so the limit is checked
 * from the words themselves.
 */
const CONSUMPTIVE_LIMIT_ASSERTED = /\bconsumptive(?:[- ]use)?\s+(?:limits?|capacity|caps?|entitlements?|rights?)\b|\bconsumptive[- ]use\s+(?:is|are|was|were|will\s+be)\s+(?:limited|capped|restricted|set)\b/i;
const SENTENCE_BREAK = /\n|[.!?](?=\s|$)/;

/**
 * The rate's own sentence, after the rate and before any other figure,
 * explicitly disclaims the role the classifier gave it, and also the
 * consumptive-use limit when the sentence asserts one before the rate. A
 * disclaimer of a different role ("…; it is not cooling consumption" after a
 * consumptive-use limit) or of a later figure does not clear it.
 */
function classifiedRoleDisclaimed(maskedText: string, quantity: ParsedWaterQuantity, role: WaterQuantityRole | null): boolean {
  if (role === null) return false;
  const head = maskedText.slice(Math.max(0, quantity.start - 300), quantity.start).split(SENTENCE_BREAK).at(-1) ?? '';
  const roles = new Set<WaterQuantityRole>([role]);
  if (CONSUMPTIVE_LIMIT_ASSERTED.test(head)) roles.add('consumptive-use-limit');
  const tail = (maskedText.slice(quantity.end, quantity.end + 300).split(SENTENCE_BREAK)[0] ?? '').split(/\d/)[0] ?? '';
  return [...roles].every(asserted => CLASSIFIED_ROLE_DISCLAIMER[asserted]?.test(tail) ?? false);
}

/** A cooling-process label ("water tower" is municipal storage, not cooling). */
const COOLING_PROCESS_LABEL_SOURCE = String.raw`\b(?:cooling|chillers?|(?<!water\s)towers?|hybrid|adiabatic|evaporat(?:e|es|ed|ing|ion|ive)|make[- ]?up|blowdown|intakes?|dedicated\s+(?:[\w-]+\s+){0,2}supply|process\s+water)\b`;
const COOLING_PROCESS_LABEL = new RegExp(COOLING_PROCESS_LABEL_SOURCE, 'gi');
/** The explicitWaterFlow negation idioms, applied to the text before a label. */
const COOLING_LABEL_NEGATED_BEFORE = /\b(?:not|no|never|without|neither|nor)\s+(?:(?:a|an|the|any|as|for)\s+){0,3}(?:[\w/-]+\s+){0,2}$|\bnon[- ]?$|\b(?:rather\s+than|instead\s+of|excluding|exclusive\s+of|separate\s+from|other\s+than|apart\s+from|distinct\s+from)\s+(?:(?:a|an|the|any)\s+)?(?:[\w/-]+\s+){0,3}$|\b(?:does|do|did|is|are|was|were|will|would)\s+not\s+(?:establish|represent|constitute|mean|imply|indicate|count\s+as)\b[^.;\n]*$|\b(?:cannot|can\s+not|must\s+not|should\s+not|may\s+not)\s+(?:be\s+)?(?:treated|described|classified|labell?ed|used|counted|read|reported|presented)\s+as\b[^.;\n]*$/i;
/** The label's own tail disclaims a quantity: "cooling makeup is not quantified". */
const COOLING_LABEL_NEGATED_TAIL = /^[^,(:;]{0,40}?\b(?:unknown|unspecified|unconfirmed|unquantified|undetermined|not\s+(?:yet\s+)?(?:known|established|determined|specified|quantified|confirmed|modell?ed))\b/i;
/** Inclusion is not identity: "must also cover fire, domestic and any cooling makeup". */
const INCLUSION_CUE = /\b(?:includ(?:e|es|ed|ing)|cover(?:s|ed|ing)?|plus|as\s+well\s+as|in\s+addition\s+to|along\s+with|together\s+with|such\s+as|among)\b/i;
/** An optional article, up to three filler words, then the cooling-process label. */
const IDENTITY_LABEL_TAIL = String.raw`\s+(?:(?:a|an|the|its|this|that)\s+)?(?<filler>(?:[\w/-]+\s+){0,3}?)(?<label>${COOLING_PROCESS_LABEL_SOURCE})`;
/** A copula that makes the rate itself a cooling quantity: "… is the dedicated cooling intake". */
const COPULA_TO_COOLING_LABEL = new RegExp(String.raw`\b(?:is|are|was|were|will\s+be|would\s+be|becomes?|serves?\s+as|acts?\s+as|functions?\s+as|counts?\s+as|represents?|constitutes?|equals?)${IDENTITY_LABEL_TAIL}`, 'gi');
/** An assignment that makes the rate itself a cooling quantity: "… is reserved as the dedicated hybrid cooling intake". */
const ASSIGNMENT_TO_COOLING_LABEL = new RegExp(String.raw`\b(?:(?:is|are|be)\s+)?(?:reserved|allocated|assigned|routed|dedicated|earmarked|set\s+aside|used|treated)\s+(?:as|for|to)${IDENTITY_LABEL_TAIL}`, 'gi');
/**
 * Copula filler that turns the label into an adequacy test, a cause, a
 * relation, a comparison or a list member, not the rate's identity: "is
 * sufficient for the hybrid cooling system", "is available for cooling", "is
 * driven by cooling", "is independent of cooling", "is a constraint on
 * cooling". A bare purpose or recipient preposition is NOT on this list: "is
 * for cooling makeup", "is consumed by the cooling towers" and "is sent to the
 * cooling towers" assign the rate to cooling.
 */
const COPULA_FILLER_NOT_IDENTITY = /\b(?:on|of|from|with|than|and|or|sufficient|insufficient|adequate|inadequate|enough|available|unavailable|needed|required|able|unable|driven|dominated|governed|determined|controlled|caused|influenced|affected|shaped|explained|independent|unrelated|related|relevant|irrelevant|tied|linked|connected|sensitive|proportional|subject|separate|distinct|likely|unlikely|expected|compared|relative|comparable|similar|larger|smaller|greater|higher|lower|more|less|above|below|beyond)\b/i;
/** A purpose or recipient preposition is identity only with assignment words around it: "is entirely for", "is consumed by", "is sent to". */
const COPULA_PREPOSITION = /\b(?:for|to|by)\b/i;
const COPULA_ASSIGNMENT_FILLER = /^(?:(?:for|to|by|the|a|an|its|entirely|solely|exclusively|only|all|wholly|fully|used|consumed|sent|supplied|delivered|routed|provided|intended|meant|drawn|taken|withdrawn|evaporated|piped|pumped|fed|directed|diverted|be|being)\s+)*$/i;
function copulaFillerIsIdentity(filler: string): boolean {
  return !COPULA_FILLER_NOT_IDENTITY.test(filler) && (!COPULA_PREPOSITION.test(filler) || COPULA_ASSIGNMENT_FILLER.test(filler));
}
/** The label is one member of a list of uses: "… for cooling, domestic and fire uses". */
const LABEL_SHARED_WITH_OTHER_USE = /^[^.;\n]{0,80}?(?:,|\b(?:and|or|plus|as\s+well\s+as|along\s+with|together\s+with)\b)\s+(?!(?:not|no|never|rather|instead|excluding|without|except)\b)(?:(?:the|any|all|both|its)\s+)?(?:[\w/-]+\s+){0,2}?(?:domestic|fire|potable|sanitary|irrigation|landscap\w*|office|non[- ]cooling|other|staff|employees?|restrooms?)\b/i;
/** An assignment to a list is not identity: "is used for domestic and cooling needs" (the prefix rule). */
const ASSIGNMENT_FILLER_LIST = /\b(?:and|or|versus|vs)\b|,/i;
const ROLE_CLAUSE_BREAK = /\n|;|\s\|\s|[.!?](?=\s|$)|\b(?:but|however|whereas|although)\b/i;

function coolingLabelNegated(clauseBefore: string, labelTail: string): boolean {
  return COOLING_LABEL_NEGATED_BEFORE.test(clauseBefore) || COOLING_LABEL_NEGATED_TAIL.test(labelTail);
}

/** A cooling-process label attached to this rate in its own clause, un-negated. */
function attachedCoolingLabel(text: string, quantity: ParsedWaterQuantity, ownRole: WaterQuantityRole | null): string | undefined {
  // Before the rate: the nearest label with no intervening rate, clause break,
  // conjunction or comparison (explicitWaterFlow adjacency). Only when the
  // classifier found no role of its own: "peak whole-site demand, including
  // cooling, is …" keeps its demand role.
  if (ownRole === null) {
    const prefix = text.slice(Math.max(0, quantity.start - 140), quantity.start).split(ROLE_CLAUSE_BREAK).at(-1) ?? '';
    const label = [...prefix.matchAll(COOLING_PROCESS_LABEL)].at(-1);
    if (label) {
      const tail = prefix.slice(label.index! + label[0].length);
      if (/^[^.;|\n\d]{0,65}$/.test(tail) && !/\b(?:and|or|versus|vs)\b/i.test(tail) && !INCLUSION_CUE.test(tail)
        && !coolingLabelNegated(prefix.slice(0, label.index), tail)) return label[0];
    }
  }
  // After the rate: an identity predicate assigning this rate to cooling. A
  // copula whose filler is a preposition, adequacy word or conjunction, and an
  // assignment to an and/or/versus list, state a purpose or a share, not the
  // rate's identity; the earliest remaining predicate decides.
  const after = maskNumberedRefTokens(text.slice(quantity.end, quantity.end + 300).split(ROLE_CLAUSE_BREAK)[0] ?? '');
  const identity = [
    ...[...after.matchAll(COPULA_TO_COOLING_LABEL)].filter(match => copulaFillerIsIdentity(match.groups!.filler!)),
    ...[...after.matchAll(ASSIGNMENT_TO_COOLING_LABEL)].filter(match => !ASSIGNMENT_FILLER_LIST.test(match.groups!.filler!)),
  ].sort((left, right) => left.index! - right.index!)[0];
  if (!identity) return undefined;
  const label = identity.groups!.label!;
  const labelStart = identity.index! + identity[0].length - label.length;
  const between = after.slice(0, labelStart);
  if (/\d/.test(between) || INCLUSION_CUE.test(between)
    || coolingLabelNegated(between, after.slice(labelStart + label.length))
    || LABEL_SHARED_WITH_OTHER_USE.test(after.slice(labelStart + label.length))) return undefined;
  return label;
}

/** A post-rate identity can mislabel an allocation even when its own prefix
 * correctly names the whole-site role. Bind within this clause, before another
 * number, and reject negation/qualification rather than scanning all prose. */
function attachedConsumptiveLimit(text: string, quantity: ParsedWaterQuantity): boolean {
  const after = maskNumberedRefTokens(text.slice(quantity.end, quantity.end + 240).split(ROLE_CLAUSE_BREAK)[0] ?? '');
  const identity = /\b(?:is|are|equals|represents|constitutes)\s+(?:(?:the|a|an|documented|permitted|available)\s+)*consumptive[- ]use\s+(?:limit|capacity|cap|entitlement|right)\b/i.exec(after);
  if (!identity) return false;
  const before = after.slice(0, identity.index);
  const tail = after.slice(identity.index + identity[0].length);
  // Only the rate's own role label (or its immediate pronoun) may bridge to
  // the identity. "... allocation supports a permit which is ..." introduces
  // another subject and cannot assert this rate's role.
  if (!/^\s*(?:(?:source[- ]stated|daily|planning|the|a|an|site|whole[- ]site|water|service|allocation|allotment|project|campus|facility|peak|average|demand|margin|and|it)\b[\s,]*)*$/i.test(before)) return false;

  return !/\d|\b(?:not|no|never|whether|if|could|may|might|would|should|must)\b/i.test(before)
    && !COOLING_LABEL_NEGATED_TAIL.test(tail) && !/^[^.!?]{0,80}\b(?:if|unless|pending|subject to)\b/i.test(tail)
    && !/^[^.!?]{0,40}\?/i.test(tail);
}

const NO_ALLOCATION_NEEDED_SOURCE = String.raw`no\s+(?:(?:additional|new|site|water|municipal|potable)\s+){0,3}(?:allocation|supply|service)\s+(?:is\s+|would\s+be\s+|will\s+be\s+)?(?:needed|required|necessary)`;
const WHOLE_SITE_WATER_CLAUSE = new RegExp(String.raw`\b(?:whole[- ]site|site[- ]wide|campus[- ]wide|facility[- ]wide|project[- ]wide|total[- ](?:site|project|campus|facility)|(?:site|campus)\s+water\s+(?:use|usage|demand|consumption|withdrawal|needs?|requirements?)|all\s+(?:site\s+)?(?:water\s+)?uses|${NO_ALLOCATION_NEEDED_SOURCE})\b`, 'gi');
/** The only whole-site conclusion a directly following consequence clause can attach to a zero. */
const NO_ALLOCATION_NEEDED_CUE = new RegExp(String.raw`\b${NO_ALLOCATION_NEEDED_SOURCE}\b`, 'gi');
const WHOLE_SITE_CUE_NEGATED_BEFORE = /\b(?:does|do|did|would|will|can|could|is|are)\s+not\s+(?:establish|represent|constitute|mean|imply|indicate|show|demonstrate|quantify|reflect|include|cover)\b[^.;\n]*$|\b(?:not|never)\s+(?:(?:a|an|the)\s+)?(?:[\w-]+\s+){0,2}$|\b(?:excluding|exclusive\s+of|separate\s+from|other\s+than)\s+(?:(?:the|any)\s+)?(?:[\w-]+\s+){0,2}$/i;
/** A comma or a clause connector ends a zero's own assertion. */
const ZERO_CLAUSE_BREAK = /\n|;|,|\s\|\s|[.!?](?=\s|$)|\b(?:but|however|whereas|although|while|so|and)\b/gi;
/**
 * Soft breaks: a comma, a bare "and" or "so" can also sit inside one clause
 * ("Total site water demand, including domestic and fire use, is 0 gal/day").
 */
const ZERO_CLAUSE_SOFT_BREAK = /^(?:,|and|so)$/i;
/**
 * A finite predicate (copula, auxiliary, modal or a common finite verb), so a
 * text span before a soft break is a complete clause of its own. Words that
 * double as nouns ("uses", "needs", "totals") are deliberately absent.
 */
const ZERO_CLAUSE_PREDICATE = /\b(?:is|are|was|were|be|been|remains|remain|rests|depends|stays|equals|has|have|had|does|do|did|will|would|can|could|may|might|must|shall|should|covers|includes|requires|consumes|exceeds|lies|falls)\b/gi;
/** A relative clause brings its own predicate: "Whole-site water use, which is driven by cooling, is 0 gal/day". */
const RELATIVE_CLAUSE_OPENER = /\b(?:which|who|whose|where)\b/gi;
/**
 * A leading contextual phrase ends at its comma: "With site-wide demand
 * outside this model, modeled cooling consumption is 0 gal/day". A scoping
 * phrase ("Across the whole site, …") does not, and keeps its cue.
 */
const CONTEXTUAL_LEAD_PHRASE = /^\s*(?:with|under|given|unlike|apart\s+from|aside\s+from|besides|beyond|outside(?:\s+of)?|except(?:\s+for)?|other\s+than|separate(?:ly)?\s+from|regarding|as\s+for|in\s+contrast\s+(?:to|with)|by\s+contrast|as\s+opposed\s+to|compared\s+(?:with|to)|relative\s+to|independent(?:ly)?\s+of|leaving|setting|notwithstanding|despite)\b|\b(?:aside|apart|notwithstanding|excepted|excluded)\s*$/i;
/** ", and modeled data-center cooling consumption is 0 gal/day": a coordinated clause with its own (non-pronoun) subject. */
const OWN_SUBJECT_PREDICATE = /^\s*(?!(?:it|this|that|these|those|they|which|its)\b)(?:[\w/-]+\s+){1,6}?(?:is|are|was|were|equals|totals|remains|stays)\b[^,;]{0,40}$/i;

/** The span since the last accepted clause start is a clause of its own (soft breaks only). */
function zeroClauseSoftBreakEnds(segment: string, breakToken: string, rest: string): boolean {
  const predicates = segment.match(ZERO_CLAUSE_PREDICATE)?.length ?? 0;
  const relatives = segment.match(RELATIVE_CLAUSE_OPENER)?.length ?? 0;
  return predicates > relatives || (breakToken === ',' && CONTEXTUAL_LEAD_PHRASE.test(segment))
    || (/^(?:and|so)$/i.test(breakToken) && /,\s*$/.test(segment) && OWN_SUBJECT_PREDICATE.test(rest));
}
/** "…, so no water allocation is needed": a consequence drawn directly from the zero. */
const CONSEQUENCE_CLAUSE_START = /^\s*,?\s*(?:(?:and\s+)?(?:so|therefore|thus|hence)|and|meaning(?:\s+that)?|which\s+means(?:\s+that)?)\b/i;
const NO_ALLOCATION_NEEDED_EXACT = new RegExp(String.raw`^${NO_ALLOCATION_NEEDED_SOURCE}$`, 'i');
/** "… is needed for cooling": a purpose phrase naming only a cooling process. */
const COOLING_PURPOSE = new RegExp(String.raw`^\s+(?:for|by)\s+(?:the\s+)?(?:[\w-]+\s+){0,2}?(?:${COOLING_PROCESS_LABEL_SOURCE}|\b(?:coolers?|heat\s+rejection|condensers?)\b)`, 'i');
const COOLING_PURPOSE_CLAUSE_END = /\n|;|[.!?](?=\s|$)|\b(?:but|however|whereas|although|though|while|so)\b/i;
/** Any other or broader use in that purpose clause makes it a site-level conclusion again. */
const NON_COOLING_USE = /\b(?:domestic|fire|potable|sanitary|irrigation|landscap\w*|office|other|all|any|anything|everything|else|whole|site|campus|facility|project|restrooms?|staff|employees?|personnel|buildings?)\b/i;

/**
 * "no water allocation is needed for cooling" (through the end of that
 * clause) is scoped to the cooling process, not a whole-site conclusion; "…
 * needed for cooling and domestic use" is not.
 */
function noAllocationScopedToCooling(rest: string): boolean {
  const purpose = COOLING_PURPOSE.exec(rest);
  if (!purpose) return false;
  return !NON_COOLING_USE.test(purpose[0] + (rest.slice(purpose[0].length).split(COOLING_PURPOSE_CLAUSE_END)[0] ?? ''));
}

/**
 * Un-negated cues in `clause`, excluding a cue whose own tail disclaims it
 * ("… is not quantified") and a no-allocation cue scoped to cooling ("no water
 * allocation is needed for cooling"). `continuation` is the text that follows
 * `clause` in the sentence; only the cooling-scope test reads it.
 */
function wholeSiteCueClaimed(clause: string, pattern: RegExp, continuation = ''): boolean {
  const scope = clause + continuation;
  return [...clause.matchAll(pattern)].some(match => {
    const end = match.index! + match[0].length;
    return !WHOLE_SITE_CUE_NEGATED_BEFORE.test(clause.slice(0, match.index))
      && !COOLING_LABEL_NEGATED_TAIL.test(clause.slice(end))
      && !(NO_ALLOCATION_NEEDED_EXACT.test(match[0]) && noAllocationScopedToCooling(scope.slice(end)));
  });
}

/**
 * The zero is stated as whole-site water only by its own clause (clause start
 * to the zero, through the end of that clause) or by a directly following
 * consequence that no allocation is needed. A whole-site cue elsewhere in the
 * sentence ("…, while site-wide demand is not quantified", "…, so whole-site
 * demand rests on the source-stated allocation") is another assertion.
 *
 * Before the zero, a hard break always starts its clause; a soft break (a
 * comma, a bare "and" or "so") does so only when the text since the last
 * accepted start already has its own predicate (beyond any relative clause's),
 * or is a leading contextual phrase ending at a comma. "Total site water
 * demand, including domestic and fire use, is 0 gal/day" keeps its whole-site
 * subject; "Site-wide water demand is outside this model, and modeled … is 0
 * gal/day" does not.
 */
function zeroStatedAsWholeSite(text: string, quantity: ParsedWaterQuantity): boolean {
  const before = text.slice(Math.max(0, quantity.start - 260), quantity.start);
  const ownStart = [...before.matchAll(ZERO_CLAUSE_BREAK)].reduce((start, match) =>
    !ZERO_CLAUSE_SOFT_BREAK.test(match[0]) || zeroClauseSoftBreakEnds(before.slice(start, match.index), match[0], before.slice(match.index! + match[0].length))
      ? match.index! + match[0].length : start, 0);
  const after = text.slice(quantity.end, quantity.end + 260);
  const ownEnd = after.search(ZERO_CLAUSE_BREAK);
  const own = before.slice(ownStart) + text.slice(quantity.start, quantity.end) + (ownEnd < 0 ? after : after.slice(0, ownEnd));
  if (wholeSiteCueClaimed(own, WHOLE_SITE_WATER_CLAUSE, ownEnd < 0 ? '' : after.slice(ownEnd))) return true;
  // Narrow pronoun carry: one whole-site assertion immediately followed by
  // ", and it is 0". Another cooling subject, negated antecedent or hard
  // clause boundary cannot lend a whole-site role to that zero.
  const carry = /,\s*(?:and\s+)?it\s+(?:is|equals|remains)\s*(?:modeled\s+(?:as\s+)?)?$/i.exec(before);
  if (carry) {
    const antecedent = before.slice(0, carry.index).split(ROLE_CLAUSE_BREAK).at(-1) ?? '';
    if (!/\b(?:cooling|not|unknown|unquantified|outside|but|whereas|while)\b/i.test(antecedent)
      && wholeSiteCueClaimed(antecedent, WHOLE_SITE_WATER_CLAUSE)) return true;
  }

  if (ownEnd < 0) return false;
  const consequence = CONSEQUENCE_CLAUSE_START.exec(after.slice(ownEnd));
  if (!consequence) return false;
  const tail = after.slice(ownEnd + consequence[0].length);
  const tailEnd = tail.search(ZERO_CLAUSE_BREAK);
  return tailEnd < 0 ? wholeSiteCueClaimed(tail, NO_ALLOCATION_NEEDED_CUE)
    : wholeSiteCueClaimed(tail.slice(0, tailEnd), NO_ALLOCATION_NEEDED_CUE, tail.slice(tailEnd));
}

const DATA_CENTER_COOLING_CUE = /\b(?:cooling|data[- ]cent(?:er|re)s?)\b/gi;
/**
 * Duty components other than data-center cooling, including generation
 * equipment that carries its own cooling water. Only generation-specific
 * words count. "BTM", a bare "condenser" (a chiller condenser-water loop) and
 * a bare "steam" (steam humidification) are data-center vocabulary too, so
 * they are not cues; steam is generation only as steam cycle, turbine, plant
 * or condenser.
 */
const OTHER_DUTY_COMPONENT_CUE = /\b(?:generat(?:ion|ors?|ing)|power[- ]plant|gas[- ]plant|plant|CCS|carbon\s+capture|total|subtotal|combined|all\s+components|turbines?|CCGT|combined[- ]cycle|steam[- ](?:cycle|turbines?|plant|(?:surface\s+)?condensers?)|surface\s+condensers?|HRSG|engines?|reciprocating|RICE|inlet\s+(?:air\s+)?cooling|power[- ]blocks?|bottoming|Rankine|main\s+condensers?)\b/i;
/** Generation words that data-center prose also uses; generation only when the clause names no data-center equipment. */
const AMBIGUOUS_GENERATION_CUE = /\b(?:BTM|steam|condensers?)\b/i;
const EXPLICIT_DATA_CENTER_CUE = /\b(?:data[- ]cent(?:er|re)s?|campus|chillers?|IT\s+load|white\s+space|servers?|humidif\w*|CRAH|CRAC)\b/gi;
const COMPONENT_CUE_NEGATED_BEFORE = /\b(?:exclud(?:e|es|ed|ing)|without|not|separate\s+from|other\s+than|apart\s+from|rather\s+than)\s+(?:(?:the|any|a|an)\s+)?(?:[\w-]+\s+){0,2}$/i;

/** The clause names data-center/cooling water (un-negated) and no other duty component. */
function namesDataCenterCoolingOnly(clause: string): boolean {
  if (OTHER_DUTY_COMPONENT_CUE.test(clause)) return false;
  if (AMBIGUOUS_GENERATION_CUE.test(clause) && ![...clause.matchAll(EXPLICIT_DATA_CENTER_CUE)].some(match =>
    !COMPONENT_CUE_NEGATED_BEFORE.test(clause.slice(0, match.index)))) return false;
  return [...clause.matchAll(DATA_CENTER_COOLING_CUE)].some(match =>
    !COMPONENT_CUE_NEGATED_BEFORE.test(clause.slice(0, match.index)));
}

function calculatedScreenBands(sed: SiteEvidenceDocument | null | undefined): AllowedBand[] {
  const screen = sed?.economicEvidence?.data?.waterUseScreening;
  if (!screen || screen.status !== 'calculated' || screen.scenarios.length === 0) return [];
  return [screen, ...(sed?.economicEvidence?.data?.waterUseScenarioScreens ?? []).map(item => item.result)]
    .filter(item => item.status === 'calculated')
    .flatMap(item => bandsFromScreen(item).map(band => ({ ...band, loadRange: item.loadRange })));
}

const ROLE_CONFLATION_EXPECTED = 'Keep the source-stated site water allocation, average/peak project water demand and allocation margin as whole-site planning quantities with their own document citation. They are not cooling-process consumption, cooling withdrawal or makeup, a dedicated cooling intake or a consumptive-use limit. State cooling consumption or makeup only from a cooling-specific frozen basis; otherwise state that it is unknown. A zero modeled cooling-process subtotal does not establish zero site water use.';

/**
 * W6 — pure cross-role check over authored prose. A daily rate equal to a
 * current, accepted whole-site ledger value (W1) that the prose assigns to a
 * cooling-process or consumptive-limit role is conflated; so is a zero that
 * only the modeled equipment/process duty supports, stated as whole-site
 * water. The deterministic projection block is skipped: W7 states its roles.
 */
function sourceRoleConflationFindings(
  text: string,
  quantities: readonly ParsedWaterQuantity[],
  report: SiteRagReport,
  sed: SiteEvidenceDocument | null | undefined,
  ledger: readonly WholeSiteWaterLedgerEntry[],
  projectionSpan: { start: number; end: number } | null,
  /** The role the source-stated role audit bound to each quantity it owns (e.g. a stated 0 MGD allocation). */
  sourceRoles: ReadonlyMap<ParsedWaterQuantity, SourceWaterRole>,
  /** Whether the frozen declared available-consumption input documents this daily rate as a consumptive-use limit. */
  documentedConsumptiveLimitAt: (galPerDay: number) => boolean,
): WaterUseScreeningFinding[] {
  const findings: WaterUseScreeningFinding[] = [];
  const maskedText = maskNumberedRefTokens(text);
  let dutyBands: AllowedBand[] | undefined;
  let screenBands: AllowedBand[] | undefined;
  for (const quantity of quantities) {
    if (insideSpan(quantity, projectionSpan) || quantity.period !== 'day' || quantity.highRaw) continue;
    if (quantity.low > 0) {
      const keys = matchWholeSiteWaterRoles(ledger, quantity.low);
      if (!keys.length) continue;
      const role = classifyWaterQuantityRole(maskedText, quantity.start, quantity.start + quantity.lowRaw.length);
      const classifiedProcess = role !== null && PROCESS_OR_LIMIT_ROLES.has(role);
      // The prose itself binds this rate to the whole-site role it equals
      // (W5: "… limited to the 75,000 gal/day source-stated planning
      // allocation"), AND either the classified role is only a bare
      // withdrawal read from the clause prefix ("Municipal withdrawals …"), or
      // the same sentence explicitly disclaims the classified role ("…; it is
      // not a consumptive-use limit"). Only then is the classifier's process
      // role not this rate's: ignore it (as UNKNOWN) and let only an attached
      // cooling label conflate the rate, e.g. "a cooling intake equal to the
      // 55,000 gal/day peak project water demand". Naming the source role is
      // not enough on its own: "Consumptive use is limited to the 75,000
      // gal/day source-stated planning site water allocation" still presents
      // the allocation as a consumptive-use limit.
      const statedRole = sourceRoles.get(quantity);
      if (
        statedRole === 'siteWastewaterAllocationMgd' ||
        statedRole === 'projectPeakWastewaterDischargeMgd' ||
        statedRole === 'projectWastewaterMarginMgd'
      ) {
        continue;
      }
      const statedWholeSiteRole = statedRole !== undefined && keys.some(key => key === statedRole)
        && (role === 'withdrawal-makeup' || classifiedRoleDisclaimed(maskedText, quantity, role)
          || classifiedRoleLinkNegated(maskedText, quantity));
      // A consumptive-use limit that the frozen scenario's own document binds
      // (lane A) is a documented limit, even where it equals a ledger value.
      const followingLimit = attachedConsumptiveLimit(text, quantity);
      const documentedLimit = (role === 'consumptive-use-limit' || followingLimit) && documentedConsumptiveLimitAt(quantity.low);
      const processRole = documentedLimit ? undefined : followingLimit ? 'consumptive-use-limit'
        : classifiedProcess && !statedWholeSiteRole ? role : undefined;
      const coolingLabel = processRole ? undefined
        : attachedCoolingLabel(text, quantity, classifiedProcess && statedWholeSiteRole ? null : role);
      if (!processRole && !coolingLabel) continue;
      const sourceLabel = keys.map(key => WHOLE_SITE_LEDGER_LABELS[key]).join(' / ');
      findings.push(finding('water-source-role-conflated', quantity.raw, quantity.excerpt, ROLE_CONFLATION_EXPECTED,
        `The report presents ${quantity.raw}, which equals the source-stated ${sourceLabel} (a whole-site quantity), as ${processRole
          ? `a ${processRole.replace(/-/g, ' ')} quantity` : `a cooling-process quantity ("${coolingLabel}")`}.`));
      continue;
    }
    // Zero: supported only by the modeled equipment/process duty or by a
    // cooling-process-only screen case (dry cooling). Neither quantifies
    // whole-site water, so neither establishes zero site water use.
    if (sourceRoles.has(quantity) || !zeroStatedAsWholeSite(text, quantity)) continue;
    dutyBands ??= bandsFromGenerationProjection(report, ledger);
    screenBands ??= calculatedScreenBands(sed);
    const supporting = [...dutyBands, ...screenBands].filter(band => matchesBand(quantity, band));
    if (!supporting.length || supporting.some(band =>
      band.measure !== 'generation-duty' && band.boundary !== 'cooling-process-only')) continue;
    findings.push(finding('water-source-role-conflated', quantity.raw, quantity.excerpt, ROLE_CONFLATION_EXPECTED,
      `The report presents the zero modeled equipment/process water subtotal ${quantity.raw} as whole-site water; a zero modeled subtotal does not establish zero site water use or that no water allocation is needed.`));
  }
  return findings;
}

function matchesBand(quantity: ParsedWaterQuantity, band: AllowedBand): boolean {
  if (quantity.period !== band.period) return false;
  if (quantity.highRaw) {
    return endpointMatches(quantity.low, quantity.lowRaw, quantity.multiplier, band.low)
      && endpointMatches(quantity.high, quantity.highRaw, quantity.multiplier, band.high);
  }
  return endpointMatches(quantity.low, quantity.lowRaw, quantity.multiplier, band.low)
    || endpointMatches(quantity.low, quantity.lowRaw, quantity.multiplier, band.high);
}

function matchesComparable(
  quantity: ParsedWaterQuantity,
  point: AllowedComparablePoint,
): boolean {
  return quantity.period === point.period
    && !quantity.highRaw
    && endpointMatches(quantity.low, quantity.lowRaw, quantity.multiplier, point.value);
}

function comparableAttribution(excerpt: string, points: readonly AllowedComparablePoint[]): boolean {
  if (EXPLICIT_COMPARABLE_PATTERN.test(excerpt)) return true;
  const normalized = excerpt.toLocaleLowerCase('en-US');
  return points.some((point) => (
    (point.facilityName.trim().length >= 3
      && normalized.includes(point.facilityName.toLocaleLowerCase('en-US')))
    || (point.projectId.trim().length >= 3
      && normalized.includes(point.projectId.toLocaleLowerCase('en-US')))
  ));
}

function explicitComparableStages(
  excerpt: string,
): Set<ComparableWaterObservationValue['stage']> {
  const stages = new Set<ComparableWaterObservationValue['stage']>();
  for (const [stage, pattern] of Object.entries(COMPARABLE_STAGE_PATTERNS) as Array<[
    ComparableWaterObservationValue['stage'],
    RegExp,
  ]>) {
    if (pattern.test(excerpt)) stages.add(stage);
  }
  return stages;
}

function isExternalProviderCapacityFact(excerpt: string): boolean {
  if (isMunicipalOperatingRateContext(excerpt)) return true;
  // "Water treatment facility" names the provider asset; it must not be
  // mistaken for the subject data-center "facility" token below.
  const providerClaim = excerpt.replace(/,\s*(?:this|which|that)\s+(?:is|would\s+be)\s+(?:insufficient|inadequate)\s+for\s+(?:the|this)\s+project['’]s\s+needs\b[\s\S]*$/i, '');
  const subjectScopeText = providerClaim.replace(
    /\b(?:water|wastewater)\s+treatment\s+facility\b/gi,
    'provider asset',
  );
  if (
    (/\b(?:parish[- ]wide|county[- ]wide|basin[- ]wide)\s+(?:(?:surface[- ]water|groundwater|total|water)\s+)*withdrawals?\b/i.test(providerClaim)
      || /\bprepared\s+for\s+another\s+(?:project|site|facility)\b[\s\S]{0,80}\bexcess\s+capacity\b/i.test(providerClaim)
      || /\b\d+(?:\.\d+)?\s*MGD\s+permitted\s+[\w\s-]{0,40}\b(?:wastewater|water|treatment)\s+(?:plant|facility)\b/i.test(providerClaim))
    && !DEFINITIVE_SITE_USE_PATTERN.test(subjectScopeText)
  ) {
    return true;
  }
  if (SUBJECT_SITE_ALLOCATION_PATTERN.test(subjectScopeText)
    || DEFINITIVE_SITE_USE_PATTERN.test(subjectScopeText)) {
    return false;
  }
  return (EXTERNAL_WATER_PROVIDER_PATTERN.test(providerClaim)
    || /\b(?:city|town|village|county|parish)\s+of\s+[a-z]/i.test(providerClaim))
    && EXTERNAL_SYSTEM_CAPACITY_PATTERN.test(providerClaim);
}

/** Audit the quoted passage with the references declared by this exact risk's
 * structured evidence envelope. The canonical report and its raw quote remain
 * unchanged. References never flow to the claim, another risk or another
 * section; the ordinary source-role/value/status checks still decide admission. */
function structuredWaterRiskAuditProjection(report: SiteRagReport): SiteRagReport {
  const water = report.waterAnalysis;
  if (!water?.risks?.length) return report;
  const risks = water.risks.map(risk => {
    if (typeof risk === 'string') return risk;
    const evidence = risk.evidence;
    if (!evidence || typeof evidence.excerpt !== 'string' || !Array.isArray(evidence.refIds)
      || !evidence.refIds.length || !evidence.refIds.every(index => {
        const reference = Number.isSafeInteger(index) && index > 0 ? report.references?.[index - 1] : undefined;
        return reference?.field === 'documents' && !!reference.documentId
          && reference.url === `doc://${reference.documentId}`;
      })) return risk;
    const citations = [...new Set(evidence.refIds)].map(index => `[Ref ${index}]`).join(' ');
    let excerpt = evidence.excerpt;
    // Bind each independent quantity context, preserving native equivalence
    // text (e.g. MGD = US gallons/day) and all assertion/status boundaries.
    const contexts = new Set<number>();
    for (const quantity of parseWaterQuantities(excerpt)) {
      const bounds = quantityContextBounds(excerpt, quantity.start, quantity.end,
        { isolateStatistics: true, isolateComparison: true });
      contexts.add(bounds.start);
    }
    for (const start of [...contexts].sort((a, b) => b - a)) {
      excerpt = excerpt.slice(0, start) + citations + ' ' + excerpt.slice(start);
    }
    return excerpt === evidence.excerpt ? risk : { ...risk, evidence: { ...evidence, excerpt } };
  });
  return { ...report, waterAnalysis: { ...water, risks } };
}

/** Typed municipal rows describe utility systems. Flattening loses the actor
 * between pipe-separated fields; exclude only their operating/capacity fields
 * from this demand guard. Carry each row's own actor into an unscoped allocation
 * label in its available field; explicit water/sewer identities stay untouched.
 * Allocation and legal-threshold claims remain checked,
 * and every other provenance/grounding guard still reads the original report. */
function waterDemandAuditProjection(report: SiteRagReport): SiteRagReport {
  const infrastructure = report.infrastructureInterconnection;
  if (!infrastructure?.municipalUtilities?.length) return report;
  return { ...report, infrastructureInterconnection: { ...infrastructure,
    municipalUtilities: infrastructure.municipalUtilities.map(row => {
      if (!/^(?:water(?:\s+supply)?|wastewater|sewer(?:age)?)(?:\s+system)?$/i.test(row.system?.trim() ?? '')) return row;
      const projected = { ...row };
      if (typeof row.available === 'string') {
        const value = row.available;
        const actor = /^(?:wastewater|sewer)/i.test(row.system.trim()) ? 'wastewater service' : 'water service';
        const boundaries = /\n|\s+\|\s+|;|[.!?](?=\s|$)|\b(?:but|however|while|whereas)\b/i;
        projected.available = value.replace(/\b(?:allocation|allotment)\b/gi, (label, offset: number) => {
          const clause = (value.slice(0, offset).split(boundaries).at(-1) ?? '')
            + label + (value.slice(offset + label.length).split(boundaries)[0] ?? '');
          // Preserve an explicit actor, including one contradicting this row;
          // only the same typed row may supply an otherwise omitted identity.
          return /\b(?:water|wastewater|sewer(?:age)?|potable)\b/i.test(clause)
            ? label : `${actor} ${label}`;
        });
      }
      for (const field of ['currentUse', 'totalCapacity'] as const) {
        const value = row[field];
        if (typeof value !== 'string') continue;
        // A later connection requirement does not change an earlier rate's
        // actor. Remove only each utility quantity from this demand projection;
        // retain site allocations and legal thresholds, including equal numbers.
        const utilityQuantities = parseWaterQuantities(value).filter(quantity =>
          !SUBJECT_SITE_ALLOCATION_PATTERN.test(quantity.context)
          && !DEFINITIVE_SITE_USE_PATTERN.test(quantity.context)
          && !isGeneralRegulatoryThresholdFact(quantity.context));
        let projectedValue = value;
        for (const quantity of utilityQuantities.sort((a, b) => b.start - a.start)) {
          projectedValue = projectedValue.slice(0, quantity.start) + projectedValue.slice(quantity.end);
        }
        projected[field] = projectedValue;
      }
      return projected;
    }),
  } };
}


/**
 * When a sentence states an existing property water-rights figure in acre-feet
 * per year (AFY) and gives its direct unit conversion in MGD or million gal/yr
 * alongside it (e.g. "343 AFY (~0.31 MGD average)" or "343 AFY (~111.77 million gal/year)"),
 * do not reject the unit conversion as an ungrounded modeled demand screen value.
 */
function isStatedAcreFeetWaterRightsConversion(quantity: ParsedWaterQuantity): boolean {
  if (quantity.low !== quantity.high) return false;
  if (quantity.period !== 'day' && quantity.period !== 'year') return false;
  const ctx = quantity.context;
  const afPattern = /\b(\d[\d,]*(?:\.\d+)?)\s*(?:acre[- ]feet(?:\s*(?:\/|per)\s*(?:year|yr))?|AFY|ac-ft\s*\/\s*yr)\b/gi;
  let match: RegExpExecArray | null;
  while ((match = afPattern.exec(ctx)) != null) {
    const af = Number((match[1] ?? '').replace(/,/g, ''));
    if (!Number.isFinite(af) || af <= 0) continue;
    const expectedGallons = quantity.period === 'year'
      ? af * 325_851
      : (af * 325_851) / 365;
    if (endpointMatches(quantity.low, quantity.lowRaw, quantity.multiplier, expectedGallons)) {
      return true;
    }
  }
  return false;
}

function isGeneralRegulatoryThresholdFact(context: string): boolean {
  if (!REGULATORY_AUTHORIZATION_PATTERN.test(context)
    || (!REGULATORY_RULE_PATTERN.test(context)
      && !GENERIC_REGULATORY_FORM_PATTERN.test(context)
      && !NAMED_WATER_REGULATOR_PATTERN.test(context))
    || !REGULATORY_THRESHOLD_PATTERN.test(context)) {
    return false;
  }

  if (SUBJECT_SITE_REGULATED_ACTIVITY_PATTERN.test(context)) return false;

  // Remove explicitly generic regulated actors before testing subject scope:
  // "any facility that withdraws ..." is a rule; "this facility will use ..."
  // is a project claim and must still pass the deterministic screen check.
  const subjectScopeText = context.replace(GENERIC_REGULATED_ACTOR_PATTERN, 'regulated actor');
  return !SUBJECT_SITE_ALLOCATION_PATTERN.test(subjectScopeText)
    && !DEFINITIVE_SITE_USE_PATTERN.test(subjectScopeText);
}

/** A cited legal document can state its own potable/discharge cap without
 * establishing the project's consumption, allocation or approved status. */
function isDocumentStatedWaterCap(quantity: ParsedWaterQuantity, references: readonly DataSource[] | undefined,
  documents: SiteEvidenceDocument['uploadedDocuments']): boolean {
  const context = quantity.context;
  if (quantity.low !== quantity.high || quantity.period !== 'day'
    || !/\b(?:ordinance|by-law|(?:zoning|municipal|water)\s+code|regulation|statute|rule|permit\s+condition|conditional\s+use\s+permit)\b/i.test(context)
    || DEFINITIVE_SITE_USE_PATTERN.test(context) || SUBJECT_SITE_ALLOCATION_PATTERN.test(context)
    || SUBJECT_SITE_REGULATED_ACTIVITY_PATTERN.test(context)) return false;
  // Status/permission is not cured by a nearby draft-instrument label.
  const withoutNegatedStatus = context.replace(/\[(?:(?:verified|inferred|unverified)\s+)?Ref[^\]]+\]/gi, '').replace(/\b(?:not|never|no)\s+(?:yet\s+)?(?:confirmed|verified|approved|permitted|authorized|secured)\b/gi, 'unknown');
  if (/\b(?:confirmed|verified|approved|permitted|authorized|secured)\b|\bpermits?\s+(?:the|this)\s+(?:site|project|campus|facility|development)\b/i.test(withoutNegatedStatus)) return false;
  const cap = /(?:\blimits?\b[^.;]{0,100}\bto|\b(?:shall|may)\s+not\s+exceed|\bnot\s+to\s+exceed|\bno\s+more\s+than|\ba\s+maximum\s+of|\bcapped\s+at|\blimited\s+to)\s*$/i;
  const rawAt = context.indexOf(quantity.raw);
  if (rawAt < 0 || !cap.test(context.slice(0, rawAt))) return false;
  const ids = sameContextReferenceIndices(context);
  if (!ids.length) return false;
  const sources = ids.map(id => references?.[id - 1]);
  if (sources.some(ref => !ref || ref.field !== 'documents' || !ref.documentId || ref.url !== `doc://${ref.documentId}`)) return false;
  const typeWords = [...context.matchAll(/\b(?:non-potable|potable|municipal|consumption|use|usage|withdrawal|draw|demand|discharge|wastewater|make-?up|recycled|greywater|groundwater|surface\s+water|cooling)\b/gi)].map(match => match[0].toLowerCase());
  if (!typeWords.length) return false;
  return sources.some(ref => {
    const doc = documents?.find(document => document.docId === ref!.documentId);
    // Positive raw-text proof only; a missing/truncated preview cannot invent
    // a cap. An exact statement in the recorded passage is sufficient here.
    const raw = [doc?.textContent ?? '', ...(doc?.pages ?? []).map(page => page.text ?? '')].join('\n');
    return parseWaterQuantities(raw).some(source => source.period === 'day'
      && source.low === quantity.low && source.high === quantity.high
      && cap.test(source.context.slice(0, source.context.indexOf(source.raw)))
      && (() => {
        const polarity = (text: string) => [...new Set([...text.matchAll(/\b(?:non[- ]potable|potable)\b/gi)]
          .map(match => /^non/i.test(match[0]) ? 'non-potable' : 'potable'))].sort().join('|');
        // 'potable' must never borrow a 'non-potable' cap (or vice versa),
        // and mixed-polarity source sentences are ambiguous for this role.
        const reportPolarity = polarity(context);
        const sourcePolarity = polarity(source.context);
        return !reportPolarity.includes('|') && !sourcePolarity.includes('|') && reportPolarity === sourcePolarity;
      })()
      && typeWords.every(word => new RegExp(`\\b${word}\\b`, 'i').test(source.context)));
  });
}

function sameContextReferenceIndices(context: string): number[] {
  return extractNumberedRefIds(context);
}

function isQualifyingWaterRegulatoryReference(reference: DataSource | undefined): boolean {
  if (!reference) return false;

  const field = String(reference.field ?? '').trim();
  const source = String(reference.source ?? '').trim();
  const url = String(reference.url ?? '').trim();
  const metadata = `${field} ${source} ${url}`;

  // Source-class exclusions are absolute. An official-looking URL copied into
  // an AI/DAM/upload row does not turn that row into a primary record.
  if (DISALLOWED_REGULATORY_REFERENCE_FIELD_PATTERN.test(field)
    || DISALLOWED_REGULATORY_REFERENCE_SOURCE_PATTERN.test(source)
    || reference.sourceGrade === 'inference'
    || reference.sourceGrade === 'regional-screening'
    || reference.sourceGrade === 'reviewed-landgate'
    || reference.qualification === 'estimated'
    || reference.qualification === 'unknown'
    || reference.qualification === 'unavailable') {
    return false;
  }

  // The legacy DataSource contract has no quote or locator. Require both an
  // audited authority/verification marker and metadata that identifies an
  // official government water-regulatory record; generic government datasets
  // (for example a FEMA map or USGS hydrology layer) cannot satisfy this gate.
  const authoritativeOrVerified = reference.sourceGrade === 'authoritative-government'
    || reference.qualification === 'verified';
  const recognizablyOfficial = OFFICIAL_GOVERNMENT_URL_PATTERN.test(url)
    || OFFICIAL_GOVERNMENT_SOURCE_PATTERN.test(source);
  return authoritativeOrVerified
    && recognizablyOfficial
    && WATER_REGULATORY_SOURCE_SCOPE_PATTERN.test(metadata)
    && WATER_REGULATORY_SOURCE_AUTHORITY_PATTERN.test(metadata);
}

function hasQualifyingSameContextWaterRegulatoryReference(
  context: string,
  references: readonly DataSource[] | undefined,
): boolean {
  return sameContextReferenceIndices(context).some((index) => (
    isQualifyingWaterRegulatoryReference(references?.[index - 1])
  ));
}

function isStatusNegated(excerpt: string): boolean {
  return NEGATED_STATUS_PATTERN.test(excerpt)
    || /\b(?:these|the)\s+(?:figures|values|bands|ranges|estimates)\s+(?:are|do)\s+not\b/i.test(excerpt);
}

/** An unidentified permitted source does not assign a permit to a modeled
 * demand. Remove only that nonquantified availability clause from the status
 * check, including its negation so it cannot excuse a separate entitlement. */
function waterStatusAssertionContext(excerpt: string): string {
  const unidentifiedSource = /\b(?:(?:(?:a|the|any)\s+)?(?:(?:viable|reliable|suitable)\s*,?\s*){0,2}permitted\s+water\s+sources?\s+(?:(?:has|have)\s+not\s+(?:yet\s+)?been\s+|(?:is|are)\s+not\s+(?:yet\s+)?)(?:identified|confirmed|established)|no\s+(?:(?:viable|reliable|suitable)\s*,?\s*){0,2}permitted\s+water\s+sources?\s+(?:has|have)\s+(?:yet\s+)?been\s+(?:identified|confirmed|established))\b/gi;
  return excerpt.replace(unidentifiedSource, (match, offset: number) => {
    const before = excerpt.slice(0, offset).split(/[.!?;|\n]|\b(?:and|but|however)\b/i).at(-1) ?? '';
    const after = excerpt.slice(offset + match.length).split(/[.!?;|\n]|\b(?:and|but|however)\b/i)[0] ?? '';
    // Do not reinterpret a quantified source, quoted/rejected proposition, or
    // a permit assertion whose predicate continues beyond this phrase.
    if (/\d/.test(before + after)
      || /\b(?:false|incorrect|untrue|not\s+true|den(?:y|ies|ied))\b/i.test(before + after)) return match;
    return '[unidentified water source]';
  });
}

/** Keep quantified status detection identical across source/model lanes. */
function hasUnnegatedWaterStatusAssertion(excerpt: string): boolean {
  const statusContext = waterStatusAssertionContext(excerpt);
  return statusContext.split(/[.!?](?=\s|$)|[;|\n]|\b(?:and|but|however)\b/i)
    .some(clause => FORBIDDEN_STATUS_PATTERN.test(clause) && !isStatusNegated(clause));
}

function expectedBandText(bands: readonly AllowedBand[]): string {
  return bands
    .filter((band) => band.period === 'day')
    .map((band) => `${band.label} [${band.boundary}; ${band.flowBasis}]: ${Math.round(band.low).toLocaleString('en-US')}-${Math.round(band.high).toLocaleString('en-US')} gal/day`)
    .join('; ')
    .slice(0, 600);
}

function finding(
  kind: WaterUseScreeningFindingKind,
  observed: string,
  excerpt: string,
  expected: string,
  message: string,
): WaterUseScreeningFinding {
  return {
    kind,
    severity: 'block',
    sectionId: 'water-use-screening',
    message,
    excerpt,
    observed,
    expected,
  };
}

/**
 * `reportFullText` can project one generated sentence through several report
 * fields. Canonicalize the sentence/record segment containing the observed
 * quantity so those identical claims produce one finding while distinct
 * claims that happen to use the same number remain independently visible.
 */
function findingDedupeKey(row: WaterUseScreeningFinding): string {
  const excerpt = row.excerpt;
  const observedAt = excerpt.toLocaleLowerCase('en-US')
    .indexOf(row.observed.toLocaleLowerCase('en-US'));
  let claim = excerpt;
  if (observedAt >= 0) {
    const leftBoundaries = [
      excerpt.lastIndexOf('|', observedAt),
      excerpt.lastIndexOf('.', observedAt - 1),
      excerpt.lastIndexOf('!', observedAt - 1),
      excerpt.lastIndexOf('?', observedAt - 1),
    ];
    const start = Math.max(...leftBoundaries) + 1;
    const rightCandidates = [
      excerpt.indexOf('|', observedAt + row.observed.length),
      excerpt.indexOf('.', observedAt + row.observed.length),
      excerpt.indexOf('!', observedAt + row.observed.length),
      excerpt.indexOf('?', observedAt + row.observed.length),
    ].filter((candidate) => candidate >= 0);
    const end = rightCandidates.length > 0 ? Math.min(...rightCandidates) + 1 : excerpt.length;
    claim = excerpt.slice(start, end);
  }
  const canonicalClaim = claim
    .replace(/^\s*(?:[-*]\s*)?[A-Z][A-Za-z0-9 /()_-]{0,40}:\s*/i, '')
    .replace(/\[Ref\s+[^\]]+\]/gi, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLocaleLowerCase('en-US');
  const canonicalObserved = row.observed.replace(/\s+/g, ' ').trim().toLocaleLowerCase('en-US');
  return `${row.kind}\u0000${canonicalObserved}\u0000${canonicalClaim}`;
}

function dedupeFindings(findings: readonly WaterUseScreeningFinding[]): WaterUseScreeningFinding[] {
  const deduped = new Map<string, WaterUseScreeningFinding>();
  for (const row of findings) deduped.set(findingDedupeKey(row), row);
  return [...deduped.values()];
}

/** A phase model establishes that a screening estimate exists, not an actual
 * demand, supply commitment, peak statistic or power-plant process-water duty.
 * Compare only explicit absence claims with calculated cases already frozen
 * in the SED. Neither a whole-campus case nor a second MW interpretation of
 * that whole-campus case establishes a phase calculation. */
/** The campus estimate cannot be substituted for a phase estimate. That valid
 * boundary becomes a false absence claim only when the same sentence insists
 * the named phase screens must be rerun despite their recorded cases. */
export function phaseWaterRerunLoads(statement: string): number[] | undefined {
  const match = /^These estimates cannot be applied to (?:the )?(?:initial|early) (.+?) phases;\s*(?:the )?water[- ]use screen must be rerun to evaluate those scenarios\.?$/i.exec(statement.trim());
  if (!match || !/^\d[\d,]*(?:\.\d+)?\s+MW(?:\s*(?:,|and|&)\s*\d[\d,]*(?:\.\d+)?\s+MW)+$/i.test(match[1]!)) return undefined;
  return [...match[1]!.matchAll(/(\d[\d,]*(?:\.\d+)?)\s+MW/gi)].map(value => Number(value[1]!.replace(/,/g, '')));
}

function phaseWaterScreenAbsenceFindings(
  text: string,
  sed: SiteEvidenceDocument | null | undefined,
): WaterUseScreeningFinding[] {
  const phaseCases = (sed?.economicEvidence?.data?.waterUseScenarioScreens ?? [])
    .flatMap((item, index) => {
      const result = item.result;
      const load = result?.loadRange;
      if (result?.status !== 'calculated' || !load?.phaseBasis
        || ['declared-project', 'whole-campus', 'unknown'].includes(load.phaseBasis)
        || !Number.isFinite(load.low) || !Number.isFinite(load.high)
        || load.low <= 0 || load.high < load.low) return [];
      const scenarios = result.scenarios.filter(row =>
        Number.isFinite(row.averageGallonsPerDay?.low)
        && Number.isFinite(row.averageGallonsPerDay?.high)
        && row.averageGallonsPerDay.low >= 0
        && row.averageGallonsPerDay.high >= row.averageGallonsPerDay.low);
      if (!scenarios.length) return [];
      return [{ item, index, load, scenarios }];
    });
  if (!phaseCases.length) return [];

  const findings: WaterUseScreeningFinding[] = [];
  const sentences = text.split(/(?<=[.!?])(?<!\bU\.S\.)\s+|\n+|\s+\|\s+/i);
  const statements = sentences.flatMap((sentence, index) => {
    const claim = sentence.trim().replace(/^(?:-\s+)?Text:\s*/i, '');
    if (phaseWaterRerunLoads(claim)) {
      const prior = sentences[index - 1] ?? '';
      if (EXPLICIT_COMPARABLE_PATTERN.test(prior)
        || /\b(?:according to|(?:study|report|assessment)\s+(?:states?|reports?|claims?|concludes?))\b/i.test(prior)) return [];
      return [sentence];
    }
    return sentence.split(/;\s*|,\s*(?:but|although|whereas|while|even though)\s+/i);
  });
  for (const raw of statements) {
    // The same sentence also appears as the claim ledger's flattened Text
    // cell; retain its prose identity so the shared deduper emits one defect.
    const statement = raw.trim().replace(/^(?:-\s+)?Text:\s*/i, '');
    if (!/\bwater\b|\bwater[- ]use\b/i.test(statement)
      || !/\b(?:model(?:ed|led|ing|ling)?|screen(?:s|ed|ing)?|estimates?|scenarios?|projections?)\b/i.test(statement)) continue;
    // This site's case inventory says nothing about a separately attributed
    // peer. A mixed clause that also denies this site's case still needs review.
    if ((EXPLICIT_COMPARABLE_PATTERN.test(statement) || /\bother[- ]project\b/i.test(statement))
      && !/\b(?:this|our|subject)\s+(?:site|project|campus|facility)\b/i.test(statement)) continue;
    // These quantities and assurances are outside the phase screen's scope.
    // Also preserve hypothetical questions and an explicitly named primary
    // screen's narrower scope: additional cases are a separate SED field.
    if (/\b(?:actual|measured|metered|committed|permitted|authorized|approved|guaranteed|peak(?:[- ]day)?|maximum[- ]day|design[- ]day|power[- ]plant|gas[- ]plant|CCGT|power generation|generation plant|construction[- ]water|offsite|off[- ]site|indirect|embodied|allocation|supply commitment|domestic|wastewater|sewage|effluent|sanitary|potable|irrigation|fire suppression|evaporative loss breakdown)\b/i.test(statement)
      || /\b(?:if|whether|incorrect|false|not true|do not claim|does not mean)\b/i.test(statement)
      || /\bprimary\s+(?:water[- ]use\s+|water\s+)?screen(?:ing)?\b|\bwaterUseScreening\b/i.test(statement)) continue;

    const phases = [...statement.matchAll(/\bphases?\s*#?\s*(\d+(?:\s*(?:,|and|&)\s*\d+)*)\b/gi)]
      .flatMap(match => [...match[1]!.matchAll(/\d+/g)].map(number => Number(number[0])));
    const loads = [...statement.matchAll(/\b(\d[\d,]*(?:\.\d+)?)\s*(MW|GW)\b/gi)]
      .map(match => Number(match[1]!.replace(/,/g, '')) * (match[2]!.toUpperCase() === 'GW' ? 1000 : 1));
    const forcedRerun = phaseWaterRerunLoads(statement);
    const phaseSubject = !!forcedRerun || /\b(?:phase[- ](?:level|specific|by[- ]phase)|individual phases?|(?:each|every) phase|per[- ]phase|phases?\s*#?\s*\d+)\b/i.test(statement);
    const absence = !!forcedRerun || /\b(?:are|is|remain(?:s)?)\s+(?:unavailable|absent|missing|not\s+(?:available|provided|included|calculated|estimated|modeled|modelled))\b|\b(?:has|have)\s+not\s+been\s+(?:provided|included|calculated|estimated|modeled|modelled)\b|\b(?:does|do)\s+not\s+(?:contain|include|provide|cover)\b[^.;!?]{0,80}\b(?:phase[- ](?:level|specific)|individual phases?|(?:each|every) phase|per[- ]phase|phases?\s*#?\s*\d+)\b|\bno\s+(?:(?:modeled|modelled|screening|phase[- ]level|phase[- ]specific|water[- ]use|water|demand|use)\s+){0,6}(?:estimates?|screens?|scenarios?|projections?)\b/i.test(statement);
    const exclusiveLoad = /\b(?:only covers?|covers? only|limited to|does not cover capacities other than)\b/i.test(statement)
      && loads.length === 1;
    if (!(phaseSubject && absence) && !exclusiveLoad) continue;

    let matchingCases = phaseCases;
    if (exclusiveLoad) {
      // "Other capacities are not covered" is contradicted only by a case
      // whose recorded load differs from the sole load named in the claim.
      matchingCases = matchingCases.filter(row => row.load.low !== loads[0] || row.load.high !== loads[0]);
    } else {
      if (phases.length) matchingCases = matchingCases.filter(row => {
        const ordinal = /\bphase\s*#?\s*(\d+)\b/i.exec(row.item.label)?.[1];
        return ordinal != null && phases.includes(Number(ordinal));
      });
      if (loads.length) matchingCases = matchingCases.filter(row => loads.some(load => load >= row.load.low && load <= row.load.high));
    }
    const hasItStatement = /\bIT[- ]load\b|\bIT case\b|\b(?:MW|GW) IT\b/i.test(statement);
    const hasFacilityStatement = /\bfacility[- ](?:load|demand)\b|\bfacility case\b|\b(?:MW|GW) facility\b/i.test(statement);
    const namedBasis = hasItStatement && !hasFacilityStatement ? 'it'
      : hasFacilityStatement && !hasItStatement ? 'facility' : null;
    if (namedBasis) matchingCases = matchingCases.filter(row => row.load.basis === namedBasis);
    // A modeled dry case cannot contradict absence of an adiabatic estimate;
    // likewise preserve absence of any other specifically unrecorded case.
    if (/\badiabatic\b/i.test(statement)) matchingCases = matchingCases.filter(row => row.scenarios.some(scenario => scenario.adiabaticWaterScreening?.status === 'calculated'));
    if (/\bwet[- ]cool(?:ing|ed)\b|\bcooling[- ]tower\b/i.test(statement)) matchingCases = matchingCases.filter(row => row.scenarios.some(scenario => scenario.kind.startsWith('wet-cooling-tower')));
    if (/\bdry[- ]cool(?:ing|ed)\b/i.test(statement)) matchingCases = matchingCases.filter(row => row.scenarios.some(scenario => scenario.kind === 'dry-cooling'));
    if (!matchingCases.length) continue;

    const recorded = matchingCases.slice(0, 6).map(({ item, index, load }) =>
      `economicEvidence.data.waterUseScenarioScreens[${index}].result (${item.label}; ${load.low}–${load.high} MW ${load.basis})`).join('; ');
    findings.push(finding(
      'recorded-phase-water-screen-absence', statement, statement,
      `Calculated phase water screening cases are recorded at ${recorded}. Use their existing scenario ranges and recorded assumptions; keep cumulative cases non-additive and distinguish modeled data-center water from actual or committed demand, utility supply, permitted withdrawal, peak demand and power-plant process water. Other phases without a calculated case remain unknown.`,
      'The report denies a phase water screening estimate or load case that is already calculated in the frozen SED.',
    ));
  }
  return findings;
}

/**
 * Report types audited ONLY for source-stated water roles (W5). Test Fit routes
 * water (main connection, fire loop, cooling supply) but carries no economic
 * water evidence, so it gets no modeled-band, phase-absence, disclosure,
 * municipal or regulatory-threshold checks. It is deliberately NOT added to
 * ECONOMIC_FACETS_BY_REPORT: that would also hand it the economic water prompt
 * contract and evidence projection.
 */
export const SOURCE_ROLE_ONLY_WATER_REPORT_TYPES: ReadonlySet<string> = new Set(['test-fit']);

/** Whether WATER-TRUTH-1 audits this report type at all. */
export function auditsSourceWaterRoles(reportType: string): boolean {
  return reportProjectsEconomicFacet(reportType, 'water') || SOURCE_ROLE_ONLY_WATER_REPORT_TYPES.has(reportType);
}

/** Pure detector. */
export function verifyWaterUseScreeningClaims(
  report: SiteRagReport,
  sed: SiteEvidenceDocument | null | undefined,
  reportType = report.reportType ?? '',
): WaterUseScreeningFinding[] {
  if (!auditsSourceWaterRoles(reportType)) return [];
  const economicWater = reportProjectsEconomicFacet(reportType, 'water');
  const text = reportFullText(waterDemandAuditProjection(structuredWaterRiskAuditProjection(report)));
  const quantities = parseWaterQuantities(text);

  // W5 — the source-stated role audit (allocation, average/peak demand and
  // margin rows: value, citation, assumed qualification, firm/approved status)
  // describes the SED source ledger, not the modeled screen. It therefore runs
  // for every audited type whether or not a calculated screen exists. Findings
  // keep their per-quantity position in the screen path below.
  const statedSourceRows = sourceWaterRows(sed);
  const sourceRoleFindings = new Map<ParsedWaterQuantity, WaterUseScreeningFinding[]>();
  const sourceRoles = new Map<ParsedWaterQuantity, SourceWaterRole>();
  for (const quantity of quantities) {
    const statement = sourceWaterStatement(text, quantity, report, statedSourceRows, sed);
    if (statement === undefined) continue;
    sourceRoleFindings.set(quantity, statement.findings);
    sourceRoles.set(quantity, statement.role);
  }
  // W6 — whole-site source quantities assigned to cooling or a consumptive
  // limit, and zero equipment duty stated as whole-site water. Also screen-free.
  const ledger = wholeSiteWaterLedger(sed);
  const projectionSpan = deterministicProjectionSpan(text, report);
  const conflationFindings = sourceRoleConflationFindings(text, quantities, report, sed, ledger, projectionSpan,
    sourceRoles, galPerDay => declaredInputProvenance(report, galPerDay) === 'documented-consumptive-limit');
  if (!economicWater) return dedupeFindings([...[...sourceRoleFindings.values()].flat(), ...conflationFindings]);

  const findings: WaterUseScreeningFinding[] = [];
  findings.push(...phaseWaterScreenAbsenceFindings(text, sed));

  // A utility's operating figures and an unsupported service conclusion can
  // occupy adjacent sentences in one authored paragraph. Sentence-only numeric
  // contexts missed the conclusion when it contained no repeated MGD value.
  // Keep each paragraph/record independent so a distant source or disclaimer
  // cannot supply evidence for another section's assertion.
  // Utility quantities excluded from the modeled-demand comparison still need
  // their original rates when checking unsupported capacity/service conclusions.
  if (sed) for (const context of new Set(reportFullText(report).split(/\n+/).map(line => line.trim()).filter(Boolean))) {
    if (municipalOperatingScopeMismatch(context, sed)) findings.push(finding('municipal-operating-scope', 'Municipal operating rates', context,
      municipalOperatingScopeQualification(sed), 'Municipal operating statistics were promoted to supply capacity or project service sufficiency without a source allocation.'));
  }

  // Regulatory thresholds are evidence claims, not project-demand inputs.
  // Check them before the SED-screen early return so a report cannot publish a
  // numeric legal trigger merely because the economic water screen is absent.
  for (const quantity of quantities) {
    if (isExternalProviderCapacityFact(quantity.providerContext)
      || !isGeneralRegulatoryThresholdFact(quantity.context)) {
      continue;
    }
    if (hasQualifyingSameContextWaterRegulatoryReference(
      quantity.context,
      report.references,
    )) {
      continue;
    }
    findings.push(finding(
      'unsupported-water-regulatory-threshold-provenance',
      quantity.raw,
      quantity.excerpt,
      WATER_REGULATORY_THRESHOLD_FALLBACK,
      `The report states the numeric water-regulatory threshold ${quantity.raw}, but the same claim does not cite a recognizably official primary water-regulatory source with authoritative or verified provenance.`,
    ));
  }

  const screen = sed?.economicEvidence?.data?.waterUseScreening;
  if (!screen || screen.status !== 'calculated' || screen.scenarios.length === 0) {
    return dedupeFindings([...findings, ...[...sourceRoleFindings.values()].flat(), ...conflationFindings]);
  }

  const screens = [screen, ...(sed?.economicEvidence?.data?.waterUseScenarioScreens ?? []).map(item => item.result)]
    .filter(item => item.status === 'calculated');
  const bands = [
    ...screens.flatMap(item => bandsFromScreen(item).map(band => ({ ...band, loadRange: item.loadRange }))),
    ...bandsFromGenerationProjection(report, ledger),
  ];
  // W6 — authored prose cannot restate a projection-only row.
  const authoredBands = bands.filter(band => !band.projectionTextOnly);
  findings.push(...combinedWaterTitleFindings(report, bands));
  const points = comparablePoints(sed!);
  const documentedPercentages = documentedConsumptivePercentages(report);
  const expected = expectedBandText(authoredBands);
  let hasModeledScreenQuantity = false;
  // P3.4 — sentences that carry a projection figure / a screen figure.
  const projectionContexts = new Set<string>();
  const screenContexts = new Set<string>();

  for (const quantity of quantities) {
    const sourceFindings = sourceRoleFindings.get(quantity);
    if (sourceFindings !== undefined) {
      findings.push(...sourceFindings);
      // An admitted source statement never substitutes for the report's
      // separate, still-required modeled screen disclosure.
      continue;
    }
    // A utility or treatment plant's system capacity is not this project's
    // demand. If the prose allocates that capacity to this site, this exclusion
    // does not apply and the quantity must still pass the exact screen check.
    if (isExternalProviderCapacityFact(quantity.providerContext)) continue;

    // The regulatory provenance pass above owns general legal thresholds.
    // They are not subject-site demand, so do not compare them to screen bands.
    // Explicit project use remains outside this classifier and reaches the
    // existing unsupported-site-water-value branch below.
    if (isGeneralRegulatoryThresholdFact(quantity.context)) continue;
    if (isDocumentStatedWaterCap(quantity, report.references, sed?.uploadedDocuments)) continue;
    if (isStatedAcreFeetWaterRightsConversion(quantity)) continue;
    const advisoryAssumptions = report.campusResourceAdvisoryAtGen?.projection?.result?.assumptions;
    if (advisoryAssumptions && quantity.period === 'day' && !quantity.highRaw
      && (quantity.low === advisoryAssumptions.normalPeakNonEvaporativeWithdrawalBudgetGpd
        || quantity.low === advisoryAssumptions.protectedWaterBudgetGpd)
      && /\b(?:campus-resource-analogy|reference[- ]case\s+(?:water\s+)?(?:ceiling|budget)|(?:routine\s+)?non[- ]evaporative\s+(?:water|withdrawal)\s+budget)\b/i.test(quantity.context)) continue;

    const statisticContext = quantityContext(text, quantity.start, quantity.end, { isolateComparison: true, isolatePhaseLoads: true, isolateStatistics: true });
    // W6 — authored prose is bound to the projection row it names: a clause
    // naming (un-negated) data-center or cooling water, and no other duty
    // component, matches only the Data-center rows; a null row is unknown.
    // The deterministic projection block keeps its own rows unchanged.
    const authored = !insideSpan(quantity, projectionSpan);
    const matchingBands = (authored ? authoredBands : bands).filter((band) => matchesBand(quantity, band)
      && !(authored && band.scenarioId === PROMOTED_WATER_DUTY_SCENARIO_ID && band.dutyComponent !== 'data-center'
        && namesDataCenterCoolingOnly(statisticContext)));
    const matchingPoints = points.filter((point) => matchesComparable(quantity, point));
    const attributedComparable = matchingPoints.length > 0
      && comparableAttribution(quantity.excerpt, matchingPoints);

    // P3.3 — a figure the promoted-scenario duty table already states is the
    // table (or a restatement of it), never a screen disclosure — even when it
    // coincides with a screen endpoint (a zero row against a dry-cooling
    // scenario's zero low, for one).
    const isProjectionFigure = matchingBands.some((band) => band.measure === 'generation-duty');
    if (isProjectionFigure) projectionContexts.add(quantity.context);
    const isScreenFigure = matchingBands.some((band) => band.measure !== 'generation-duty');
    if (isScreenFigure) screenContexts.add(quantity.context);

    // P3.6 — MW-scope enforcement used to be prompt-only: a band correctly
    // quoted but attached to a phase load outside the screen's recorded MW
    // interval passed every check. The sentence's own MW figure decides.
    if (authored && isScreenFigure && !attributedComparable) {
      const matchingScreenBands = matchingBands.filter(band => band.measure !== 'generation-duty' && band.loadRange);
      // Ordinary prose carries the same boundary as a formal case label.
      // Keep the match within this quantity's load clause and require either
      // an explicit load/demand label or MW/GW adjacent to the actor; a generic
      // mention of the facility elsewhere does not declare a facility MW basis.
      const hasItLoad = /\bIT[- ](?:load|demand)(?: case)?\b|\b(?:MW|GW)[- ]+(?:of\s+)?IT\b/i.test(quantity.loadContext);
      const hasFacilityLoad = /\bfacility[- ](?:demand|load)(?: case)?\b|\b(?:MW|GW)[- ]+(?:(?:total|gross)\s+)?facility\b/i.test(quantity.loadContext);
      const namedBasis = hasItLoad && !hasFacilityLoad ? 'it'
        : hasFacilityLoad && !hasItLoad ? 'facility' : null;
      const inScope = matchingScreenBands.some(band =>
        outOfScopeLoadInContext(quantity.loadContext, band.loadRange!) == null
        && (namedBasis == null || band.loadRange!.basis === namedBasis));
      const outOfScope = outOfScopeLoadInContext(quantity.loadContext, matchingScreenBands[0]?.loadRange ?? screen.loadRange);
      if (!inScope && (outOfScope != null || namedBasis != null)) {
        const expectedMsg = `The matching modeled cases cover ${matchingScreenBands.map(band => `${band.loadRange!.low}-${band.loadRange!.high} MW ${band.loadRange!.basis}`).filter((value, index, values) => values.indexOf(value) === index).join('; ')}; quote the case that actually matches this load and boundary.`;
        const msg = `The report attaches the screen band ${quantity.raw} to ${outOfScope != null ? `a ${outOfScope.toLocaleString('en-US')} MW load` : `the ${namedBasis} case`} outside every matching case's recorded MW scope or boundary.`;
        if (!isIsolatedFromOutOfScopeWaterLoad(quantity.excerpt, quantity.raw, `${expectedMsg} ${msg}`)) {
          findings.push(finding(
            'water-band-out-of-scope-load',
            quantity.raw,
            quantity.excerpt,
            expectedMsg,
            msg,
          ));
          continue;
        }
      }
    }
    if (!isProjectionFigure
      && matchingBands.length > 0
      && !attributedComparable
      && MODELED_CAVEAT_PATTERN.test(quantity.excerpt)) {
      hasModeledScreenQuantity = true;
    }

    if (matchingBands.length === 0 && !attributedComparable) {
      findings.push(finding(
        'unsupported-site-water-value',
        quantity.raw,
        quantity.excerpt,
        expected,
        `The report states ${quantity.raw}, but that value is not an endpoint or band in the deterministic SED water-use screen and is not an explicitly attributed policy-valid comparable observation.`,
      ));
      continue;
    }

    // Comparable records preserve their stage. A policy-valid reported actual
    // may be repeated as an explicitly attributed actual, but cannot be
    // relabeled as the amount applied for or permitted (and vice versa).
    // When prose is stage-neutral, attribution + the exact structured value is
    // sufficient; we block only an explicit contradiction.
    if (attributedComparable) {
      const statedStages = explicitComparableStages(quantity.excerpt);
      const supportedStages = new Set(matchingPoints.map((point) => point.stage));
      if (statedStages.size > 0 && ![...statedStages].some((stage) => supportedStages.has(stage))) {
        findings.push(finding(
          'comparable-water-stage-mislabeled',
          quantity.raw,
          quantity.excerpt,
          `The structured comparable record supports ${[...supportedStages].join(' or ')} for this exact value; application requests, permit limits, design estimates, and reported actual use remain distinct.`,
          `The report attributes ${quantity.raw} to a policy-valid comparable but describes it at a water-record stage that does not match the structured evidence.`,
        ));
      }
      continue;
    }

    const statedFlow = quantityIsCalledConsumptiveUse(text, quantity) ? 'consumption' : explicitWaterFlow(text, quantity);
    const flowBands = /\bmodern\s+operator[- ]fleet\b/i.test(statisticContext)
      ? matchingBands.filter(band => band.scenarioId.startsWith('modern-operator-fleet')) : matchingBands;
    // W6 — a projection figure is flow-checked too when the projection rows it
    // matched carry only one flow ("cooling withdrawal … 0" matching only the
    // zero data-center CONSUMPTION row while data-center withdrawal is unknown).
    const singleFlowProjectionFigure = authored && isProjectionFigure
      && new Set(flowBands.filter(band => band.measure === 'generation-duty').map(band => band.flowBasis)).size === 1;
    if (((isScreenFigure && !isProjectionFigure) || singleFlowProjectionFigure) && statedFlow
      && flowBands.length > 0 && !flowBands.some(band => band.flowBasis === statedFlow)) {
      findings.push(finding('water-flow-basis-mislabeled', quantity.raw, quantity.excerpt,
        `Preserve the matching modeled flow basis (${[...new Set(flowBands.map(band => band.flowBasis))].join(' or ')}). An unspecified withdrawal-versus-consumption numerator must remain total onsite water use with that limitation, not be relabeled as withdrawal or consumption.`,
        `The report labels ${quantity.raw} as ${statedFlow}, which the matching frozen water model does not establish.`));
    }
    // An exact data-center screening number is still wrong when assigned to
    // a power plant. Equipment duty projections have their own allowed bands.
    const powerPlantActor = /\b(?:power[- ]plant|gas[- ]plant|generation\s+(?:plant|facility)|(?:gas|nuclear)[- ](?:fired\s+)?(?:generation|power)\s+(?:plant|facility)|wet[- ]cooled\s+BTM\s+(?:technolog(?:y|ies)|options?)|CCGT\s+(?:cooling|water))\b/i;
    const negatedPlantActor = /\b(?:exclud(?:e[sd]?|ing)|without|not|separate\s+from)\b[^.;]{0,60}\b(?:power[- ]plant|gas[- ]plant|generation\s+(?:plant|facility))\b/i;
    const followingContext = text.slice(quantity.end, quantity.end + 700).split('\n')[0] ?? '';
    const paragraphStart = text.lastIndexOf('\n', quantity.start) + 1;
    const precedingContext = text.slice(Math.max(paragraphStart, quantity.start - 500), quantity.start);
    const inheritsPlantScenario = /\bwet[- ]cooled\s+BTM\s+(?:technolog(?:y|ies)|options?)\b/i.test(precedingContext)
      && !/\bdata[- ]cent(?:er|re)\b/i.test(statisticContext)
      && !negatedPlantActor.test(statisticContext);
    const combinedEndUse = /\b(?:cooling|data[- ]cent(?:er|re))\s+and\s+(?:power\s+generation|power[- ]plant)\s+(?:needs|demand|requirements)\b/i.test(precedingContext)
      && !/\b(?:excluding|excludes|separate from|not including)\b/i.test(statisticContext);
    const transfersToPlantFeasibility = followingContext.split(/(?<=[.!?])\s+/).some(sentence =>
      /\b(?:this|the)\s+(?:water\s+)?(?:uncertainty|demand|requirement|risk)\b[^.!?]{0,220}\b(?:render|make)\b[^.!?]{0,100}\b(?:BTM|combined[- ]cycle gas|gas[- ]plant)\b[^.!?]{0,100}\b(?:infeasible|unviable)\b/i.test(sentence)
      && !/\b(?:not|cannot|never)\b[^.!?]{0,45}\b(?:render|make)\b/i.test(sentence));
    if (isScreenFigure && !isProjectionFigure
      && ((powerPlantActor.test(statisticContext)
        && !negatedPlantActor.test(statisticContext)
        && !/\bdata[- ]cent(?:er|re)\b/i.test(statisticContext))
        || transfersToPlantFeasibility || combinedEndUse || inheritsPlantScenario)) {
      findings.push(finding(
        'water-end-use-mislabeled', quantity.raw, quantity.excerpt,
        'This SED water-use screen models data-center water only. Quote it with that boundary; a separate power-plant process-water estimate requires its own equipment and duty basis.',
        `The report assigns the data-center screening value ${quantity.raw} to a power-plant water scenario.`,
      ));
    }
    const statisticContextForPeak = (quantity.period === 'hour'
      ? statisticContext
      : statisticContext.replace(/\bpeak\s+active[- ]hour(?:s)?(?:\s+makeup(?:\s+rate)?)?/gi, 'active-hour'))
      .replace(/\bpeak(?:-proxy|\s+(?:dry[- ]bulb|wet[- ]bulb|summer|ambient|temperature|temperatures|month|monthly|heat))\b/gi, 'design-weather');
    const claimsPeak = /\b(?:peak(?:[- ]day)?|maximum[- ]day|design[- ]day)\b/i.test(statisticContextForPeak)
      && !/\b(?:not|never|no)\b[^.;]{0,45}\b(?:peak(?:[- ]day)?|maximum[- ]day|design[- ]day)\b/i.test(statisticContextForPeak);
    const hasPeakBasis = matchingBands.some(band =>
      band.measure === 'generation-duty' || /:adiabatic-.*peak(?:-day)?$/.test(band.scenarioId));
    if (claimsPeak && !hasPeakBasis) {
      findings.push(finding(
        'water-statistic-mislabeled', quantity.raw, quantity.excerpt,
        'Preserve the source statistic: annual-average daily use and 100%-load sensitivity do not establish peak or design-day demand.',
        `The report calls ${quantity.raw} peak or design-day demand, but the matching screening evidence supports an average or load sensitivity only.`,
      ));
    }

    // The compact excerpt is diagnostic only: it may crop the beginning of
    // this same modeled assertion. Classify its original sentence/record
    // context, retaining the existing field boundaries and status predicates.
    const claimContext = maskNumberedRefTokens(quantity.context);
    const forbiddenStatus = hasUnnegatedWaterStatusAssertion(claimContext);
    const definitiveUse = DEFINITIVE_SITE_USE_PATTERN.test(claimContext)
      && !MODELED_CAVEAT_PATTERN.test(claimContext);
    const lacksScreeningDisclosure = !(
      MODELED_CAVEAT_PATTERN.test(claimContext)
      || (!authored && /\bscreened-configuration duty\b/i.test(claimContext))
    );
    if (forbiddenStatus || definitiveUse || lacksScreeningDisclosure) {
      findings.push(finding(
        'modeled-water-mislabeled',
        quantity.raw,
        quantity.excerpt,
        'Label every subject-site SED quantity as a modeled screening range; it is not actual, measured, metered, permitted, requested, committed, or guaranteed water use.',
        `The report presents the SED water-use value ${quantity.raw} without a clear modeled/screening qualifier or with an unsupported actual, permitted, measured, requested, design, or committed status.`,
      ));
    }
  }
  findings.push(...conflationFindings);

  // P3.4 — the two water models differ by construction (equipment/process
  // boundary vs site-boundary WUE). A sentence that states a figure of each
  // and presents them as equivalent is a reconciliation the projection
  // forbids; the deterministic basis line already says why they differ.
  for (const context of projectionContexts) {
    if (!screenContexts.has(context)) continue;
    if (!WATER_BASIS_EQUIVALENCE_PATTERN.test(context)) continue;
    findings.push(finding(
      'water-basis-conflated',
      context.slice(0, 120),
      context.slice(0, 420),
      'State the scenario duty and the screening band separately, each with its own basis; never as equivalent, converted, summed or reconciled quantities.',
      'The report presents a promoted-scenario water duty and a site-boundary screening band as the same quantity; they are modeled at different boundaries and are not comparable.',
    ));
  }

  CONSUMPTIVE_PERCENT_PATTERN.lastIndex = 0;
  let percentMatch: RegExpExecArray | null;
  while ((percentMatch = CONSUMPTIVE_PERCENT_PATTERN.exec(text)) != null) {
    const excerpt = compactExcerpt(text, percentMatch.index, percentMatch.index + percentMatch[0].length);
    const context = quantityContext(text, percentMatch.index, percentMatch.index + percentMatch[0].length);
    const low = parseNumber(percentMatch[1] ?? '');
    const high = parseNumber(percentMatch[2] || percentMatch[1] || '');
    const normalizedLow = Math.min(low, high);
    const normalizedHigh = Math.max(low, high);
    const attributedDeveloperDocument = DEVELOPER_DOCUMENT_ATTRIBUTION_PATTERN.test(context)
      && documentedPercentages.some((range) => (
        range.low === normalizedLow && range.high === normalizedHigh
      ));
    if (attributedDeveloperDocument) continue;
    findings.push(finding(
      'unsupported-consumptive-percentage',
      percentMatch[0],
      excerpt,
      'Neither the deterministic SED water-use screen, structured comparable evidence, nor an exactly matching frozen developer-document claim supplies this consumptive-loss percentage.',
      `The report states ${percentMatch[0]}, but no structured policy-valid subject-site, comparable, or explicitly attributed frozen developer-document consumptive-loss percentage supports it.`,
    ));
  }

  // Feasibility now owns the screening answer; legacy Water Resource reports keep the same disclosure contract. A calculated
  // SED screen that disappears entirely from reader-visible prose is a blocking
  // omission, even when the report happens not to invent a conflicting value.
  // Other water-aware reports may legitimately discuss a different decision
  // facet without restating the bands; their claims are still validated above.
  // A different water violation must not conceal this separate required repair.
  if ((reportType === 'water-resource' || reportType === 'feasibility')
    && !hasModeledScreenQuantity) {
    findings.push(finding(
      'missing-water-screen-disclosure',
      'No modeled SED water-use quantity found',
      text.replace(/\s+/g, ' ').trim().slice(0, 420) || '(report has no reader-visible narrative)',
      expected,
      'The water-screen report omits the calculated deterministic SED water-use screen. Reproduce at least one exact in-scope scenario quantity with its modeled/screening qualifier; the report prompt requires the complete scenario bands and caveat.',
    ));
  }

  return dedupeFindings(findings);
}

/** Stamp (or clear) the guard's deterministic QA projection. */
export function applyWaterUseScreeningGuard(
  report: SiteRagReport,
  sed: SiteEvidenceDocument | null | undefined,
  reportType = report.reportType ?? '',
): SiteRagReport {
  if (!auditsSourceWaterRoles(reportType)) return report;
  const findings = verifyWaterUseScreeningClaims(report, sed, reportType);
  // A source-role-only audit (Test Fit) never reads the modeled screen, so it
  // records no screen versions rather than implying the screen was checked.
  const screen = reportProjectsEconomicFacet(reportType, 'water')
    ? sed?.economicEvidence?.data?.waterUseScreening
    : undefined;
  return {
    ...report,
    qaFindings: {
      ...(report.qaFindings ?? {}),
      waterUseScreening: {
        findings,
        screenModelVersion: screen?.modelVersion ?? null,
        screenSourceCatalogVersion: screen?.sourceCatalogVersion ?? null,
      },
    },
  };
}

export function isIsolatedFromOutOfScopeWaterLoad(
  excerpt: string,
  observed: string,
  expectedMessage?: string,
): boolean {
  if (!excerpt || !observed) return false;
  const idx = excerpt.indexOf(observed);
  if (idx === -1) return false;
  let isolated = quantityContext(excerpt, idx, idx + observed.length, {
    isolatePhaseLoads: true,
  });
  // When compactExcerpt truncated the leading Phase 1 header ("…"), a trailing
  // ", while Phase 2 (...)" clause after the observed quantity still starts a
  // sibling phase clause.
  const obsInIsolated = isolated.indexOf(observed);
  if (obsInIsolated !== -1) {
    const afterObs = isolated.slice(obsInIsolated + observed.length);
    const trailingPhaseClause = /[,;)]?\s*\b(?:while|whereas)\s+phase\s*#?\s*\d+\b/i.exec(afterObs);
    if (trailingPhaseClause && trailingPhaseClause.index != null) {
      isolated = isolated.slice(0, obsInIsolated + observed.length + trailingPhaseClause.index);
    }
  }
  const hasItLoad = /\bIT[- ](?:load|demand)(?: case)?\b|\b(?:MW|GW)[- ]+(?:of\s+)?IT\b/i.test(isolated);
  const hasFacilityLoad = /\bfacility[- ](?:demand|load)(?: case)?\b|\b(?:MW|GW)[- ]+(?:(?:total|gross)\s+)?facility\b/i.test(isolated);
  const isolatedNamedBasis = hasItLoad && !hasFacilityLoad ? 'it'
    : hasFacilityLoad && !hasItLoad ? 'facility' : null;
  const allAllowedRanges = [...(expectedMessage?.matchAll(/(\d[\d,.]*)-(\d[\d,.]*)\s*MW(?:\s+(facility|it))?/gi) ?? [])]
    .map((m) => ({
      low: Number(m[1]!.replace(/,/g, "")),
      high: Number(m[2]!.replace(/,/g, "")),
      basis: m[3]?.toLowerCase(),
    }))
    .filter((r) => Number.isFinite(r.low) && Number.isFinite(r.high));
  if (
    allAllowedRanges.length > 0 &&
    allAllowedRanges.some(
      (r) =>
        outOfScopeLoadInContext(isolated, r) === null &&
        (isolatedNamedBasis == null || r.basis == null || r.basis === isolatedNamedBasis),
    )
  ) {
    return true;
  }
  const wrongLoadMatch =
    expectedMessage?.match(/Do\s+not\s+attach\s+it\s+to\s+(\d[\d,.]*)\s*MW/i) ??
    expectedMessage?.match(/attaches\s+the\s+screen\s+band\s+.*?\s+to\s+a\s+(\d[\d,.]*)\s*MW\s+load/i);
  if (wrongLoadMatch?.[1]) {
    const wrongMw = Number(wrongLoadMatch[1].replace(/,/g, ""));
    if (Number.isFinite(wrongMw)) {
      const loadsInIsolated = [...isolated.matchAll(/(\d[\d,.]*)\s*MW\b/gi)]
        .map((m) => Number(m[1]!.replace(/,/g, "")))
        .filter((n) => Number.isFinite(n));
      return !loadsInIsolated.some((n) => Math.abs(n - wrongMw) < 0.01);
    }
  }
  return false;
}
